import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme, colorSchemes } from '@/context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import SendPostItDialog from '@/components/SendPostItDialog';
import Navbar from '@/components/layout/Navbar';
import {
  Heart, HandHelping, MapPin, Plus, Loader2, Search,
  AlertTriangle, X, Map, List, Navigation, WifiOff, RefreshCw, Download, Bookmark, Trash2,
  Footprints, Bike, Car, Sun, Moon, Fuel, ChevronUp, Flashlight, Droplets, Home, Zap,
  Flame, Construction, BatteryLow, Share2
} from 'lucide-react';

import { API, CATEGORIES, RESOURCE_CATEGORIES, ICON_FILTERS, MARKER_COLORS, RADIUS_OPTIONS } from '@/components/crisis/constants';
import { StatsBanner } from '@/components/crisis/StatsBanner';
import { CategoryRing } from '@/components/crisis/CategoryRing';
import { PostCard } from '@/components/crisis/PostCard';
import { ResourceCard } from '@/components/crisis/ResourceCard';
import { VoiceNavPanel } from '@/components/crisis/VoiceNavPanel';
import { speakWithFallback } from '@/lib/offline-tts';
import { ElevationProfile } from '@/components/crisis/ElevationProfile';
import { VehicleProfile } from '@/components/crisis/VehicleProfile';
import { FuelPlanner } from '@/components/crisis/FuelPlanner';
import { ExploreMode } from '@/components/crisis/ExploreMode';
import { ResourceMap } from '@/components/crisis/ResourceMap';
import useHeading from '@/hooks/useHeading';
import CrisisWelcomeModal from '@/components/crisis/CrisisWelcomeModal';
import SOSFlasher from '@/components/crisis/SOSFlasher';
import NearbyBeacon from '@/components/crisis/NearbyBeacon';
import { IncidentReport } from '@/components/crisis/IncidentReport';
import { RoadClosureReport } from '@/components/crisis/RoadClosureReport';
import { IncidentProximityAlert } from '@/components/crisis/IncidentProximityAlert';
import { BatterySaver } from '@/components/crisis/BatterySaver';
import SpeedLimitSign, { fetchSpeedLimit } from '@/components/crisis/SpeedLimitSign';
import { SEO } from '@/components/SEO';


// Resilient dynamic import with chunk error recovery
async function loadOfflineRouter() {
  try {
    const mod = await import('../lib/offline-router.js');
    return mod.OfflineRouter;
  } catch (err) {
    if (err.message && err.message.includes('Loading chunk')) {
      // Chunk hash mismatch — only reload if actually online
      if (navigator.onLine) {
        try {
          const names = await caches.keys();
          await Promise.all(names.filter(n => n.startsWith('rollm8s')).map(n => caches.delete(n)));
        } catch {}
        window.location.reload();
      }
    }
    throw err;
  }
}


// Pre-cache OSRM routes to nearby resources for offline navigation
async function cacheNearbyRoutes(userLocation, resources, onProgress, mode = 'foot') {
  const haversine = (a, b) => {
    const R = 6371000;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  };

  // Filter resources within 80 miles (~128,748 metres), sort by distance
  // Skate spots are always included first, then fill remaining slots with nearest others
  const all = resources
    .filter(r => r.lat && r.lng)
    .map(r => ({ ...r, dist: haversine(userLocation, r) }))
    .filter(r => r.dist <= 128748);

  const skateSpots = all.filter(r => r.category === 'skate_spots').sort((a, b) => a.dist - b.dist);
  const others = all.filter(r => r.category !== 'skate_spots').sort((a, b) => a.dist - b.dist);
  const remaining = Math.max(0, 200 - skateSpots.length);
  const withDist = [...skateSpots, ...others.slice(0, remaining)];

  const cached = {};
  const BATCH = 5;
  let done = 0;

  for (let i = 0; i < withDist.length; i += BATCH) {
    const batch = withDist.slice(i, i + BATCH);
    await Promise.all(batch.map(async (r) => {
      const key = `${r.lat.toFixed(5)},${r.lng.toFixed(5)}`;
      try {
        const url = `https://router.project-osrm.org/route/v1/${mode}/${userLocation.lng},${userLocation.lat};${r.lng},${r.lat}?overview=full&geometries=geojson&steps=true`;
        const resp = await fetch(url);
        if (!resp.ok) return;
        const data = await resp.json();
        if (data.code !== 'Ok' || !data.routes?.[0]) return;
        const route = data.routes[0];
        cached[key] = {
          coords: route.geometry.coordinates.map(c => ({ lat: c[1], lng: c[0] })),
          distance: route.distance,
          duration: route.duration,
          steps: route.legs?.[0]?.steps?.map(s => ({
            text: s.maneuver?.instruction || s.name || 'Continue',
            distance: s.distance,
            time: s.duration,
            type: s.maneuver?.type || 'turn',
            latLng: s.maneuver?.location ? { lat: s.maneuver.location[1], lng: s.maneuver.location[0] } : null,
          })) || [],
          fromLat: userLocation.lat,
          fromLng: userLocation.lng,
        };
      } catch { /* skip failed routes */ }
      done++;
      if (onProgress) onProgress(done, withDist.length);
    }));
    // Delay between batches to respect OSRM rate limits
    if (i + BATCH < withDist.length) await new Promise(r => setTimeout(r, 800));
  }

  try {
    localStorage.setItem('crisis_cached_routes', JSON.stringify(cached));
  } catch { /* storage full */ }
  return Object.keys(cached).length;
}

// Look up a cached OSRM route for a destination
function getCachedRoute(destLat, destLng) {
  try {
    const cached = JSON.parse(localStorage.getItem('crisis_cached_routes') || '{}');
    const key = `${destLat.toFixed(5)},${destLng.toFixed(5)}`;
    if (cached[key]) return cached[key];
    // Fuzzy match — find route within 200m of destination
    for (const [k, v] of Object.entries(cached)) {
      const [lat, lng] = k.split(',').map(Number);
      const dlat = (lat - destLat) * 111320;
      const dlng = (lng - destLng) * 111320 * Math.cos(destLat * Math.PI / 180);
      if (Math.sqrt(dlat * dlat + dlng * dlng) < 200) return v;
    }
  } catch {}
  return null;
}

export default function CrisisZonePage() {
  const { user } = useAuth();
  const { colorScheme, setModeOverride, clearModeOverride } = useTheme();
  const themeC = colorSchemes[colorScheme];
  const c1 = themeC.gradientFrom;
  const c2 = themeC.gradientTo;
  const navigate = useNavigate();
  const { heading, needsCalibration: rawNeedsCalibration } = useHeading();
  const [calibrationDismissed, setCalibrationDismissed] = useState(false);
  const needsCalibration = rawNeedsCalibration && !calibrationDismissed;

  // Auto-dismiss compass calibration after 8 seconds
  useEffect(() => {
    if (!rawNeedsCalibration) { setCalibrationDismissed(false); return; }
    const t = setTimeout(() => setCalibrationDismissed(true), 8000);
    return () => clearTimeout(t);
  }, [rawNeedsCalibration]);
  const [pageMode, setPageMode] = useState('dark');
  const [tab, setTab] = useState('board');
  const [posts, setPosts] = useState([]);
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState({ active_needs: 0, active_offers: 0, total_resources: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showAddResource, setShowAddResource] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resourceView, setResourceView] = useState('map');
  const [listLimit, setListLimit] = useState(50);
  const [resourceSearch, setResourceSearch] = useState('');
  const [resourceCategoryFilter, setResourceCategoryFilter] = useState('');
  const [postitOpen, setPostitOpen] = useState(false);
  const [postitUser, setPostitUser] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [savedOffline, setSavedOffline] = useState(!!localStorage.getItem('crisis_resources_cache'));
  const [osmSyncing, setOsmSyncing] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [radiusMiles, setRadiusMiles] = useState(20);
  const [locating, setLocating] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [locationLabel, setLocationLabel] = useState('');
  // Tracks whether the OS-level location permission was denied so we can
  // show a single friendly banner instead of repeating red toasts on every
  // action that needs GPS (Near Me, Get Directions, postcode search, etc.)
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);

  // Voice navigation state
  const [navDestination, setNavDestination] = useState(null);
  const [waypoint, setWaypoint] = useState(null); // current waypoint stop
  const finalDestinationRef = useRef(null); // original destination when waypoint is active
  const [navSteps, setNavSteps] = useState(null);
  const [navSummary, setNavSummary] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const [dropPinMode, setDropPinMode] = useState(false);
  const [routeCoords, setRouteCoords] = useState(null);
  const [navSearch, setNavSearch] = useState('');
  const [navSearching, setNavSearching] = useState(false);
  const [transportMode, setTransportMode] = useState('car'); // car | bike | foot
  const [mapOpen, setMapOpen] = useState(false); // whether full map view is open
  const [showTransportPicker, setShowTransportPicker] = useState(false); // transport mode popup on map open
  const watchIdRef = useRef(null);
  const rerouteTimerRef = useRef(null);
  const mapRef = useRef(null);
  const gpsThrottleRef = useRef(0);
  const userLocationRef = useRef(null);
  const offRouteCountRef = useRef(0);
  const voiceOnRef = useRef(true);

  const [pendingCategorySwitch, setPendingCategorySwitch] = useState(null);
  const [showResumeNav, setShowResumeNav] = useState(false);
  const [cachedRoute, setCachedRoute] = useState(null);
  const [vehicleProfile, setVehicleProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('crisis_vehicle_profile')) || null; } catch { return null; }
  });
  const [showFuelStations, setShowFuelStations] = useState(false);
  const [fuelStations, setFuelStations] = useState([]);
  const [savedLocations, setSavedLocations] = useState(() => {
    try { return JSON.parse(localStorage.getItem('crisis_saved_locations') || '[]'); } catch { return []; }
  });
  const [savingLocation, setSavingLocation] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [pendingSaveName, setPendingSaveName] = useState('');
  const [pendingSaveCategory, setPendingSaveCategory] = useState('');
  const [pendingSaveDescription, setPendingSaveDescription] = useState('');
  const [pendingSaveCustomCategory, setPendingSaveCustomCategory] = useState('');
  const [tileDownloading, setTileDownloading] = useState(false);
  const [tileProgress, setTileProgress] = useState({ done: 0, total: 0 });
  const [routeCacheProgress, setRouteCacheProgress] = useState(null);
  const [showSyncPopup, setShowSyncPopup] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showSOSFlasher, setShowSOSFlasher] = useState(false);
  const [syncPhase, setSyncPhase] = useState('idle'); // idle | tiles | routes | graph | done
  const [syncRoutesDone, setSyncRoutesDone] = useState(0);
  const [syncRoutesTotal, setSyncRoutesTotal] = useState(0);
  const [syncRoutesCount, setSyncRoutesCount] = useState(0);
  const [graphProgress, setGraphProgress] = useState({ phase: '', done: 0, total: 0 });
  const [hillWarnings, setHillWarnings] = useState([]);
  const [mapFollowPaused, setMapFollowPaused] = useState(false);
  const [navPanelMinimized, setNavPanelMinimized] = useState(false);
  const [navUIVisible, setNavUIVisible] = useState(true);
  const navUITimerRef = useRef(null);
  const [newPost, setNewPost] = useState({ type: 'need', category: 'food', title: '', description: '', location_text: '', contact_method: '', urgent: false });
  const [newResource, setNewResource] = useState({ name: '', category: 'food', description: '', address: '', hours: '' });

  // ── Heatmap / Incidents / Road Closures / Evacuation / Battery Saver ──
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapPoints, setHeatmapPoints] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [showIncidentReport, setShowIncidentReport] = useState(false);
  const [roadClosures, setRoadClosures] = useState([]);
  const [showClosures, setShowClosures] = useState(true);
  const [showClosureReport, setShowClosureReport] = useState(false);
  const [evacuationRoutes, setEvacuationRoutes] = useState([]);
  const [showEvacRoutes, setShowEvacRoutes] = useState(true);
  const [batterySaverActive, setBatterySaverActive] = useState(false);
  const [currentSpeedLimit, setCurrentSpeedLimit] = useState(null);

  // Page-specific theme override — force dark mode, restore on leave
  useLayoutEffect(() => {
    setModeOverride('dark');
    return () => clearModeOverride();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle updates the override directly
  useEffect(() => {
    setModeOverride(pageMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageMode]);
  // Keep userLocation ref in sync (for stable callbacks)
  useEffect(() => { userLocationRef.current = userLocation; }, [userLocation]);

  // Auto-hide nav UI 3 seconds after showing, reappear on tap
  const showNavUI = useCallback(() => {
    setNavUIVisible(true);
    if (navUITimerRef.current) clearTimeout(navUITimerRef.current);
    navUITimerRef.current = setTimeout(() => setNavUIVisible(false), 3000);
  }, []);

  useEffect(() => {
    if (navDestination) showNavUI();
    return () => { if (navUITimerRef.current) clearTimeout(navUITimerRef.current); };
  }, [navDestination, showNavUI]);

  // Listen for routing errors from MapComponents
  useEffect(() => {
    const errorHandler = () => {
      toast.error('Could not calculate route — try a closer resource');
    };
    const directHandler = (e) => {
      const { bearing, distance, name } = e.detail;
      toast.info(`Direct route: Head ${bearing} for ${distance} to ${name}`, { duration: 5000 });
    };
    window.addEventListener('crisis-route-error', errorHandler);
    window.addEventListener('crisis-direct-route', directHandler);
    return () => {
      window.removeEventListener('crisis-route-error', errorHandler);
      window.removeEventListener('crisis-direct-route', directHandler);
    };
  }, []);

  // Track syncPhase in a ref so the SW message handler always reads the latest value
  const syncPhaseRef = useRef(syncPhase);
  useEffect(() => { syncPhaseRef.current = syncPhase; }, [syncPhase]);

  // Listen for SW tile download progress
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'TILE_PROGRESS') {
        setTileProgress({ done: event.data.done, total: event.data.total });
      } else if (event.data?.type === 'TILE_COMPLETE') {
        setTileDownloading(false);
        setTileProgress({ done: 0, total: 0 });
        // If sync popup is active, auto-start routes
        if (syncPhaseRef.current === 'tiles') {
          setSyncPhase('tiles_done');
        } else {
          // Tiles cached silently — no toast needed
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  // Check for cached route on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem('crisis_active_route');
      if (cached) {
        const data = JSON.parse(cached);
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          setCachedRoute(data);
          setShowResumeNav(true);
        } else {
          localStorage.removeItem('crisis_active_route');
        }
      }
    } catch { /* corrupt cache */ }
  }, []);

  // Save active route to localStorage when navigation is active
  useEffect(() => {
    if (navDestination && navSteps && routeCoords) {
      try {
        localStorage.setItem('crisis_active_route', JSON.stringify({
          destination: navDestination,
          steps: navSteps,
          summary: navSummary,
          routeCoords,
          timestamp: Date.now(),
        }));
      } catch { /* storage full */ }
      // Also pre-cache tiles along the route at navigation zoom levels
      if (navigator.serviceWorker?.controller) {
        // Cache at zoom 16, 17, and 18 — navigation zooms to 17-19 near turns
        for (const zoom of [16, 17, 18]) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CACHE_ROUTE_TILES',
            coords: routeCoords.filter((_, i) => i % (zoom > 16 ? 2 : 3) === 0),
            zoom,
          });
        }
      }
      // Directions panel stays open — user closes it manually
    }
  }, [navDestination, navSteps, navSummary, routeCoords]);

  // Music pause prompt before entering nav
  const [showMusicPrompt, setShowMusicPrompt] = useState(false);
  const musicPromptAction = useRef(null);

  const checkMusicBeforeNav = useCallback((proceed) => {
    if (window._rollm8sMusicPlaying) {
      musicPromptAction.current = proceed;
      setShowMusicPrompt(true);
    } else {
      proceed();
    }
  }, []);

  const handleMusicPromptYes = useCallback(() => {
    window.dispatchEvent(new CustomEvent('pauseMusicForNav'));
    setShowMusicPrompt(false);
    if (musicPromptAction.current) musicPromptAction.current();
    musicPromptAction.current = null;
  }, []);

  const handleMusicPromptNo = useCallback(() => {
    setShowMusicPrompt(false);
    if (musicPromptAction.current) musicPromptAction.current();
    musicPromptAction.current = null;
  }, []);

  const resumeCachedRoute = useCallback(() => {
    if (!cachedRoute) return;
    if (!userLocationRef.current) {
      if (locationPermissionDenied) {
        toast.error('Allow location access first — see the banner above.');
      } else {
        toast.error('Tap "Near Me" first so we can route you from your current spot.');
      }
      return;
    }
    setNavDestination(cachedRoute.destination);
    setNavSteps(cachedRoute.steps);
    setNavSummary(cachedRoute.summary);
    setRouteCoords(cachedRoute.routeCoords);
    setTab('resources');
    setResourceView('map');
    setMapOpen(true);
    setShowResumeNav(false);
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const speed = pos.coords.speed; // m/s or null
        window.dispatchEvent(new CustomEvent('crisis-gps', { detail: { lat, lng, speed } }));
        const now = Date.now();
        if (now - gpsThrottleRef.current > 15000) {
          gpsThrottleRef.current = now;
          setUserLocation({ lat, lng });
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
    );
    toast.success('Resumed navigation');
  }, [cachedRoute]);

  // Phase 1: Sync map tiles via service worker
  const syncMapTiles = useCallback(async () => {
    setSyncPhase('tiles');
    setTileDownloading(true);
    const center = userLocation || { lat: 51.509, lng: -0.118 };
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_TILES',
        lat: center.lat,
        lng: center.lng,
        zoom: 14,
      });
    } else {
      // No SW controller — skip to routes
      setSyncPhase('tiles_done');
    }
  }, [userLocation]);

  const syncResourcesRef = useRef([]);

  // Phase 2: Cache nearby routes
  const syncRoutes = useCallback(async () => {
    setSyncPhase('routes');
    const allResources = syncResourcesRef.current.length > 0 ? syncResourcesRef.current : resources;
    if (!userLocation || allResources.length === 0) {
      setSyncPhase('graph');
      return;
    }
    setSyncRoutesDone(0);
    setSyncRoutesTotal(0);
    const count = await cacheNearbyRoutes(userLocation, allResources, (done, total) => {
      setSyncRoutesDone(done);
      setSyncRoutesTotal(total);
    }, transportMode);
    setSyncRoutesCount(count);
    setSyncPhase('graph');
  }, [resources, userLocation, transportMode]);

  // Phase 3: Download road graph + postcode DB for offline A* navigation
  const syncGraph = useCallback(async () => {
    try {
      const OfflineRouter = await loadOfflineRouter();

      // Delta version check: skip download if client already has latest graph
      const updateCheck = await OfflineRouter.checkForUpdate(API);
      if (!updateCheck.needsDownload) {
        toast.success('Road graph is already up to date');
        // Still check postcodes
        const hasPC = await OfflineRouter.hasPostcodes();
        if (!hasPC) {
          setGraphProgress({ phase: 'postcodes', done: 0, total: 1 });
          await OfflineRouter.downloadPostcodes(API, (p) => setGraphProgress({ phase: 'postcodes_' + p.phase, ...p }));
        }
        const hasPL = await OfflineRouter.hasPlaces();
        if (!hasPL) {
          setGraphProgress({ phase: 'places', done: 0, total: 1 });
          await OfflineRouter.downloadPlaces(API, (p) => setGraphProgress({ phase: 'places_' + p.phase, ...p }));
        }
        setSyncPhase('done');
        localStorage.setItem('crisis_offline_synced', new Date().toISOString());
        return;
      }

      // Need download — either new install or update available
      if (updateCheck.reason === 'update_available') {
        toast.info(`Road graph update available (${updateCheck.sizeMB} MB)`);
      }

      setGraphProgress({ phase: 'downloading', done: 0, total: 0 });
      const result = await OfflineRouter.download(API, (p) => setGraphProgress(p));

      if (result.skipped) {
        toast.success('Road graph is already up to date — skipped download');
      } else {
        // Verify the graph actually saved correctly
        const verify = await OfflineRouter.getInfo();
        if (!verify.available) {
          throw new Error('Graph download appeared to succeed but data is missing. Please try again.');
        }
        toast.success(`Road graph downloaded: ${(verify.nodes || 0).toLocaleString()} nodes`);
      }

      // Now download postcodes
      setGraphProgress({ phase: 'postcodes', done: 0, total: 1 });
      await OfflineRouter.downloadPostcodes(API, (p) => setGraphProgress({ phase: 'postcodes_' + p.phase, ...p }));

      // Download UK places gazetteer (67K+ place names for offline search)
      const hasPlaces = await OfflineRouter.hasPlaces();
      if (!hasPlaces) {
        setGraphProgress({ phase: 'places', done: 0, total: 1 });
        await OfflineRouter.downloadPlaces(API, (p) => setGraphProgress({ phase: 'places_' + p.phase, ...p }));
      }

      setSyncPhase('done');
      localStorage.setItem('crisis_offline_synced', new Date().toISOString());
    } catch (err) {
      console.error('Graph/postcode/places download failed:', err);
      // If chunk loading failed, try clearing SW cache and retry once
      if (err.message && err.message.includes('Loading chunk')) {
        toast.info('Updating app files, please wait...', { duration: 5000 });
        try {
          if ('serviceWorker' in navigator) {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg) { await reg.update(); }
          }
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.filter(n => n.startsWith('rollm8s')).map(n => caches.delete(n)));
          window.location.reload();
          return;
        } catch { /* fall through to error toast */ }
      }
      toast.error(`Download failed: ${err.message}`, { duration: 15000 });
      setSyncPhase('idle');
      // Do NOT mark as synced — the data is missing
    }
  }, []);

  // Auto-start route caching when tiles complete
  useEffect(() => {
    if (syncPhase === 'tiles_done') syncRoutes();
  }, [syncPhase, syncRoutes]);

  // Auto-start graph download when routes complete
  useEffect(() => {
    if (syncPhase === 'graph') syncGraph();
  }, [syncPhase, syncGraph]);

  // ── Fetch heatmap / incidents / closures / evacuation data ──
  const fetchCrisisLayers = useCallback(async () => {
    try {
      const [heatRes, closureRes, evacRes, incidentRes] = await Promise.all([
        axios.get(`${API}/crisis/heatmap`).catch(() => ({ data: { points: [] } })),
        axios.get(`${API}/crisis/road-closures`).catch(() => ({ data: { closures: [] } })),
        axios.get(`${API}/crisis/evacuation-routes`).catch(() => ({ data: { routes: [] } })),
        axios.get(`${API}/crisis/incidents`).catch(() => ({ data: { incidents: [] } })),
      ]);
      setHeatmapPoints(heatRes.data.points || []);
      setRoadClosures(closureRes.data.closures || []);
      setEvacuationRoutes(evacRes.data.routes || []);
      setIncidents(incidentRes.data.incidents || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCrisisLayers();
    const iv = setInterval(fetchCrisisLayers, 60000); // refresh every 60s
    return () => clearInterval(iv);
  }, [fetchCrisisLayers]);

  const handleUpvoteClosure = useCallback(async (closureId) => {
    if (!user) { toast.error('Login to confirm closures'); return; }
    try {
      await axios.post(`${API}/crisis/road-closures/${closureId}/upvote`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` } });
      toast.success('Closure confirmed');
      fetchCrisisLayers();
    } catch { toast.error('Failed to confirm'); }
  }, [user, fetchCrisisLayers]);

  const handleIncidentVote = useCallback(async (incidentId, vote) => {
    try {
      const endpoint = vote === 'yes' ? 'upvote' : 'downvote';
      const res = await axios.post(`${API}/crisis/incidents/${incidentId}/${endpoint}`);
      if (res.data.auto_resolved) {
        toast.success('Incident cleared by community votes');
      } else {
        toast.success(vote === 'yes' ? 'Confirmed — thanks!' : 'Noted — thanks for the update!');
      }
      fetchCrisisLayers();
    } catch { toast.error('Could not submit vote'); }
  }, [fetchCrisisLayers]);

  const handleGetDirections = useCallback((dest) => {
    if (!userLocationRef.current) {
      if (locationPermissionDenied) {
        toast.error('Allow location access to get directions — see the banner above.');
      } else {
        toast.error('Tap "Near Me" first so we know where to route you from.');
      }
      return;
    }
    // Warm up speech engine (requires user gesture on mobile)
    try {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        // Speak a space character (empty string doesn't trigger engine on all browsers)
        const warmup = new SpeechSynthesisUtterance(' ');
        warmup.volume = 0.01; // Near-silent, 0 is ignored by some engines
        warmup.rate = 10; // Fastest possible to finish quickly
        window.speechSynthesis.speak(warmup);
        console.log('[TTS] Warmup triggered via user gesture');
      }
    } catch {}
    setNavDestination(dest);
    setNavSteps(null);
    setNavSummary(null);
    setRouteCoords(null);
    setDropPinMode(false);
    setNavPanelMinimized(false);
    setMapOpen(true);
    setResourceView('map');
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const speed = pos.coords.speed; // m/s or null
        const gpsHeading = pos.coords.heading; // degrees 0-360, null if stationary
        // Fire DOM event for map components (no React re-render)
        window.dispatchEvent(new CustomEvent('crisis-gps', { detail: { lat, lng, speed, gpsHeading } }));
        // Update React state very infrequently for UI labels
        const now = Date.now();
        if (now - gpsThrottleRef.current > 15000) {
          gpsThrottleRef.current = now;
          setUserLocation({ lat, lng });
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
    );
  }, []);

  const handleNavSearch = useCallback(async () => {
    const q = navSearch.trim();
    if (!q) return;
    setNavSearching(true);
    try {
      // Check if it looks like a UK postcode
      const pcMatch = q.replace(/\s+/g, '').match(/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i);
      if (pcMatch) {
        // Try online first, then offline fallback
        let found = false;
        try {
          const res = await axios.get(`https://api.postcodes.io/postcodes/${encodeURIComponent(q)}`);
          if (res.data?.result) {
            const { latitude, longitude, admin_district, postcode: pc } = res.data.result;
            handleGetDirections({ lat: latitude, lng: longitude, name: `${pc} (${admin_district || ''})` });
            setNavSearch('');
            found = true;
          }
        } catch { /* online failed — try offline */ }

        if (!found) {
          // Offline postcode lookup
          try {
            const OfflineRouter = await loadOfflineRouter();
            const result = await OfflineRouter.lookupPostcode(q);
            if (result) {
              handleGetDirections({ lat: result.lat, lng: result.lng, name: result.postcode });
              setNavSearch('');
              found = true;
            }
          } catch { /* offline lookup failed too */ }
        }

        if (found) { setNavSearching(false); return; }
      }

      // Fallback to Nominatim for road names / addresses (online)
      try {
        const res = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: { q: q + ', UK', format: 'json', limit: 1, addressdetails: 1 },
          headers: { 'User-Agent': 'RollM8s-CrisisZone/1.0' },
        });
        if (res.data?.length > 0) {
          const place = res.data[0];
          handleGetDirections({ lat: parseFloat(place.lat), lng: parseFloat(place.lon), name: place.display_name.split(',').slice(0, 2).join(',') });
          setNavSearch('');
          setNavSearching(false);
          return;
        }
      } catch { /* nominatim failed — try offline gazetteer */ }

      // Offline place name lookup (67K+ UK places from IndexedDB)
      try {
        const OfflineRouter = await loadOfflineRouter();
        const place = await OfflineRouter.searchPlaces(q);
        if (place) {
          handleGetDirections({ lat: place.lat, lng: place.lng, name: place.name });
          setNavSearch('');
          setNavSearching(false);
          return;
        }
      } catch { /* places gazetteer not downloaded yet */ }

      // Offline street name search (uses 400K road names from downloaded graph)
      try {
        const OfflineRouter = await loadOfflineRouter();
        const street = await OfflineRouter.searchStreet(q, userLocation?.lat, userLocation?.lng);
        if (street) {
          handleGetDirections({ lat: street.lat, lng: street.lng, name: street.name });
          setNavSearch('');
          setNavSearching(false);
          return;
        }
      } catch { /* graph not downloaded or search failed */ }

      // Everything failed
      toast.error('Location not found. Make sure offline data is downloaded for offline search.');
    } catch {
      toast.error('Search failed — try a postcode or place name');
    }
    setNavSearching(false);
  }, [navSearch, handleGetDirections]);

  // Save a custom location for offline use — geocode + pre-cache OSRM route
  const handleSaveLocation = useCallback(async () => {
    const q = navSearch.trim();
    if (!q || !userLocationRef.current) {
      if (!q) toast.error('Enter a postcode or address');
      else if (locationPermissionDenied) toast.error('Allow location access first — see the banner above.');
      else toast.error('Tap "Near Me" first so we know where to route you from.');
      return;
    }
    setSavingLocation(true);
    try {
      let lat, lng, name;
      // Geocode
      const pcMatch = q.replace(/\s+/g, '').match(/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i);
      if (pcMatch) {
        try {
          const res = await axios.get(`https://api.postcodes.io/postcodes/${encodeURIComponent(q)}`);
          if (res.data?.result) {
            lat = res.data.result.latitude;
            lng = res.data.result.longitude;
            name = `${res.data.result.postcode} (${res.data.result.admin_district || ''})`;
          }
        } catch { /* fall through */ }
      }
      if (!lat) {
        const res = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: { q: q + ', UK', format: 'json', limit: 1, addressdetails: 1 },
          headers: { 'User-Agent': 'RollM8s-CrisisZone/1.0' },
        });
        if (res.data?.length > 0) {
          lat = parseFloat(res.data[0].lat);
          lng = parseFloat(res.data[0].lon);
          name = res.data[0].display_name.split(',').slice(0, 2).join(',');
        }
      }
      if (!lat || !lng) { toast.error('Location not found'); setSavingLocation(false); return; }

      // Pre-cache OSRM route
      let routeData = null;
      try {
        const url = `https://router.project-osrm.org/route/v1/${transportMode}/${userLocationRef.current.lng},${userLocationRef.current.lat};${lng},${lat}?overview=full&geometries=geojson&steps=true`;
        const resp = await fetch(url);
        if (resp.ok) {
          const data = await resp.json();
          if (data.code === 'Ok' && data.routes?.[0]) {
            const route = data.routes[0];
            routeData = {
              coords: route.geometry.coordinates.map(c => ({ lat: c[1], lng: c[0] })),
              distance: route.distance,
              duration: route.duration,
              steps: route.legs?.[0]?.steps?.map(s => ({
                text: s.maneuver?.instruction || s.name || 'Continue',
                distance: s.distance,
                time: s.duration,
                type: s.maneuver?.type || 'turn',
                latLng: s.maneuver?.location ? { lat: s.maneuver.location[1], lng: s.maneuver.location[0] } : null,
              })) || [],
              fromLat: userLocation.lat,
              fromLng: userLocation.lng,
            };
            // Also save to the main route cache (keyed by transport mode)
            try {
              const cached = JSON.parse(localStorage.getItem('crisis_cached_routes') || '{}');
              cached[`${lat.toFixed(5)},${lng.toFixed(5)}_${transportMode}`] = routeData;
              localStorage.setItem('crisis_cached_routes', JSON.stringify(cached));
            } catch { /* storage full */ }
          }
        }
      } catch { /* route fetch failed */ }

      const loc = { id: Date.now().toString(), name: q, resolvedName: name, lat, lng, savedAt: new Date().toISOString() };
      const updated = [...savedLocations.filter(l => !(Math.abs(l.lat - lat) < 0.0001 && Math.abs(l.lng - lng) < 0.0001)), loc];
      setSavedLocations(updated);
      localStorage.setItem('crisis_saved_locations', JSON.stringify(updated));

      setNavSearch('');
      toast.success(`Saved "${q}"`);

      // If logged in, ask if they want to share publicly
      if (user) {
        setPendingSaveName(q);
        setShowSavePrompt(true);
      }
    } catch {
      toast.error('Could not save location — check your connection');
    }
    setSavingLocation(false);
  }, [navSearch, savedLocations, transportMode, user]);

  const handlePublicSave = useCallback(async () => {
    const loc = savedLocations[savedLocations.length - 1];
    if (!loc || !user) return;
    const cat = pendingSaveCategory === 'other_custom' ? 'other' : pendingSaveCategory;
    const desc = pendingSaveCategory === 'other_custom'
      ? `Suggested category: ${pendingSaveCustomCategory}. ${pendingSaveDescription}`.trim()
      : pendingSaveDescription;
    try {
      // Save as a proper community resource so it shows on the map
      await axios.post(`${API}/crisis/resources`, {
        name: loc.resolvedName || loc.name,
        category: cat || 'other',
        description: desc || '',
        address: loc.name,
        lat: loc.lat,
        lng: loc.lng,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` } });
      // Also save to public saved locations for offline caching
      await axios.post(`${API}/crisis/saved-locations`, {
        name: loc.name, lat: loc.lat, lng: loc.lng, resolved_name: loc.resolvedName
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` } });
      const updated = savedLocations.map(l => l.id === loc.id ? { ...l, isPublic: true } : l);
      setSavedLocations(updated);
      localStorage.setItem('crisis_saved_locations', JSON.stringify(updated));
      toast.success('Shared as a community resource');
    } catch {
      toast.error('Could not share — try again');
    }
    setShowSavePrompt(false);
    setPendingSaveCategory('');
    setPendingSaveDescription('');
    setPendingSaveCustomCategory('');
  }, [savedLocations, user, pendingSaveCategory, pendingSaveDescription, pendingSaveCustomCategory]);

  const handlePrivateSave = useCallback(() => {
    setShowSavePrompt(false);
    setPendingSaveCategory('');
    setPendingSaveDescription('');
    setPendingSaveCustomCategory('');
    // Saved silently
  }, []);

  const deleteSavedLocation = useCallback((id) => {
    const updated = savedLocations.filter(l => l.id !== id);
    setSavedLocations(updated);
    localStorage.setItem('crisis_saved_locations', JSON.stringify(updated));
    toast.success('Location removed');
  }, [savedLocations]);

  const handleNavSteps = useCallback((steps, summary) => {
    console.log('[VoiceEngine] handleNavSteps called — steps:', steps?.length, 'summary:', summary?.totalDistance?.toFixed(0), 'm');
    setNavSteps(steps);
    setNavSummary(summary);
  }, []);

  const handleNavClear = useCallback(() => {
    window.speechSynthesis?.cancel();
    setNavDestination(null);
    setNavSteps(null);
    setNavSummary(null);
    setRouteCoords(null);
    setHillWarnings([]);
    setMapFollowPaused(false);
    setNavUIVisible(true);
    setCurrentSpeedLimit(null);
    if (navUITimerRef.current) clearTimeout(navUITimerRef.current);
    localStorage.removeItem('crisis_active_route');
    if (watchIdRef.current) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    if (rerouteTimerRef.current) { clearTimeout(rerouteTimerRef.current); rerouteTimerRef.current = null; }
    offRouteCountRef.current = 0;
  }, []);

  const handleMapRef = useCallback((map) => { mapRef.current = map; }, []);

  // Keep voiceOnRef synced with state
  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);

  // Rerouting: fast detection, flexible direction
  const prevGpsRef = useRef(null);
  const headingRef = useRef(0);
  const lastRerouteTimeRef = useRef(0);
  const lastSpeedRef = useRef(0);

  useEffect(() => {
    if (!routeCoords || !navDestination) return;
    const routeCoordsSnap = routeCoords;

    const distToRoute = (lat, lng) => {
      let minDist = Infinity;
      const pY = lat * 111000;
      const cosLat = Math.cos(lat * Math.PI / 180);
      const pX = lng * 111000 * cosLat;
      for (let i = 0; i < routeCoordsSnap.length - 1; i++) {
        const aY = routeCoordsSnap[i].lat * 111000;
        const aX = routeCoordsSnap[i].lng * 111000 * cosLat;
        const bY = routeCoordsSnap[i + 1].lat * 111000;
        const bX = routeCoordsSnap[i + 1].lng * 111000 * cosLat;
        const dx = bX - aX, dy = bY - aY;
        const lenSq = dx * dx + dy * dy;
        const t = lenSq > 0 ? Math.max(0, Math.min(1, ((pX - aX) * dx + (pY - aY) * dy) / lenSq)) : 0;
        const d = Math.sqrt((pX - (aX + t * dx)) ** 2 + (pY - (aY + t * dy)) ** 2);
        if (d < 20) return d;
        if (d < minDist) minDist = d;
      }
      if (routeCoordsSnap.length === 1) {
        const c = routeCoordsSnap[0];
        return Math.sqrt((pY - c.lat * 111000) ** 2 + (pX - c.lng * 111000 * cosLat) ** 2);
      }
      return minDist;
    };

    const handler = (e) => {
      const { lat, lng, speed, gpsHeading } = e.detail;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      // Use browser's GPS heading (degrees, 0=north) if available — most reliable
      if (Number.isFinite(gpsHeading) && gpsHeading !== null) {
        headingRef.current = gpsHeading * Math.PI / 180; // store as radians
      } else {
        const prev = prevGpsRef.current;
        if (prev) {
          const dlat = lat - prev.lat;
          const dlng = lng - prev.lng;
          const moved = Math.sqrt(dlat * dlat + dlng * dlng) * 111000;
          if (moved > 3) {
            headingRef.current = Math.atan2(dlng * Math.cos(lat * Math.PI / 180), dlat);
          }
        }
      }
      prevGpsRef.current = { lat, lng };
      const speedMps = (speed && Number.isFinite(speed) && speed > 0) ? speed : 0;
      lastSpeedRef.current = speedMps;

      // Update speed limit sign from real OSM data
      fetchSpeedLimit(lat, lng).then(limit => {
        if (limit) setCurrentSpeedLimit(prev => prev === limit ? prev : limit);
      });

      // 1s cooldown — match Google Maps reroute speed
      if (Date.now() - lastRerouteTimeRef.current < 1000) return;

      const dist = distToRoute(lat, lng);
      const threshold = Math.max(50, 50 + speedMps * 2);

      if (dist > threshold) {
        offRouteCountRef.current++;
        // 1 reading = instant reroute on first detection
        if (offRouteCountRef.current >= 1 && !rerouteTimerRef.current) {
          const currentPos = prevGpsRef.current || { lat, lng };
          const currentSpeed = lastSpeedRef.current;
          const headingRad = headingRef.current;
          const headingDeg = ((headingRad * 180 / Math.PI) + 360) % 360;
          lastRerouteTimeRef.current = Date.now();
          toast.info('Rerouting...', { duration: 1500 });
          speakWithFallback('Rerouting.', 'straight', voiceOnRef.current);

          // Project start point AHEAD in driving direction
          const projectMetres = Math.max(60, currentSpeed * 3);
          const projectedLat = currentPos.lat + (Math.cos(headingRad) * projectMetres) / 111000;
          const projectedLng = currentPos.lng + (Math.sin(headingRad) * projectMetres) / (111000 * Math.cos(currentPos.lat * Math.PI / 180));

          console.log(`[Reroute] heading=${headingDeg.toFixed(0)}° speed=${currentSpeed.toFixed(1)}m/s project=${projectMetres.toFixed(0)}m`);

          setUserLocation({ lat: projectedLat, lng: projectedLng });
          setRouteCoords(null);
          setNavSteps(null);
          setCurrentSpeedLimit(null);
          // Pass heading so RoutingControl can tell OSRM which way we're going
          setNavDestination(prev => prev ? { ...prev, _reroute: Date.now(), _bearing: headingDeg } : null);
          offRouteCountRef.current = 0;
        }
      } else {
        offRouteCountRef.current = 0;
        if (rerouteTimerRef.current) {
          clearTimeout(rerouteTimerRef.current);
          rerouteTimerRef.current = null;
        }
      }
    };

    window.addEventListener('crisis-gps', handler);
    return () => {
      window.removeEventListener('crisis-gps', handler);
      if (rerouteTimerRef.current) {
        clearTimeout(rerouteTimerRef.current);
        rerouteTimerRef.current = null;
      }
    };
  }, [routeCoords, navDestination]);


  // ── Voice Navigation Engine ── Always active during navigation (not tied to panel visibility)
  // Google Maps-style: 400m warning → 150m warning → 40m "now" → arrival at 50m
  const voiceSpokenRef = useRef(new Set());
  const voiceStepsRef = useRef(null);
  const prevNavStepsRef = useRef(null);
  useEffect(() => { voiceStepsRef.current = navSteps; }, [navSteps]);

  // Reset voice step keys on reroute (navSteps: null → new steps)
  // Keep 'intro' flag so full intro isn't re-spoken; the reroute handler already says "Rerouting"
  useEffect(() => {
    if (navSteps?.length && !prevNavStepsRef.current?.length && voiceSpokenRef.current.has('intro')) {
      // This is a reroute — clear step keys, keep intro blocked
      voiceSpokenRef.current = new Set(['intro']);
    }
    prevNavStepsRef.current = navSteps;
  }, [navSteps]);

  // Intro announcement when route is first calculated
  useEffect(() => {
    console.log('[VoiceEngine] Intro check — steps:', navSteps?.length, 'voiceOn:', voiceOn, 'dest:', navDestination?.name, 'hasIntro:', voiceSpokenRef.current.has('intro'));
    if (!navSteps?.length || !voiceOn || !navDestination) return;
    if (voiceSpokenRef.current.has('intro')) return;
    voiceSpokenRef.current = new Set();
    voiceSpokenRef.current.add('intro');
    const totalDist = navSummary ? `${(navSummary.totalDistance / 1000).toFixed(1)} kilometres` : '';
    const totalTime = navSummary ? `about ${Math.round(navSummary.totalTime / 60)} minutes` : '';
    const firstStep = navSteps[0]?.text || '';
    const introText = `Navigating to ${navDestination.name || 'destination'}. ${totalDist}, ${totalTime}. ${firstStep}`;
    console.log('[VoiceEngine] Speaking intro:', introText.slice(0, 80));
    speakWithFallback(introText, 'depart', voiceOn);
  }, [navSteps, voiceOn, navDestination, navSummary]);

  // Turn-by-turn announcements via real-time GPS
  // Only announce the CURRENT upcoming step sequentially — no overlap
  const currentNavStepRef = useRef(0);
  const voiceGraceUntilRef = useRef(0); // suppress voice right after reroute
  useEffect(() => {
    currentNavStepRef.current = 0;
    // Brief grace after reroute to prevent immediate re-announce (1s)
    voiceGraceUntilRef.current = Date.now() + 1000;
    console.log('[VoiceEngine] Grace period set for 1s, step index reset to 0');
  }, [navSteps]);

  useEffect(() => {
    if (!voiceOn || !navDestination) return;
    const handler = (e) => {
      const { lat, lng, speed } = e.detail;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const curSteps = voiceStepsRef.current;
      if (!curSteps?.length) return;

      // Grace period after reroute — don't announce anything
      if (Date.now() < voiceGraceUntilRef.current) return;

      const idx = currentNavStepRef.current;
      if (idx >= curSteps.length) return;
      const step = curSteps[idx];
      if (!step?.latLng) return;

      const dist = Math.sqrt(
        Math.pow((lat - step.latLng.lat) * 111000, 2) +
        Math.pow((lng - step.latLng.lng) * 111000 * Math.cos(lat * Math.PI / 180), 2)
      );

      const isArrival = step.type === 'arrive' || step.type === 'DestinationReached' || idx === curSteps.length - 1;

      if (isArrival) {
        if (dist < 50 && !voiceSpokenRef.current.has('arrive')) {
          voiceSpokenRef.current.add('arrive');
          if (waypoint && finalDestinationRef.current) {
            speakWithFallback('Waypoint reached. Now navigating to destination.', 'depart', voiceOn);
            toast.success('Waypoint reached — continuing to destination');
            const finalDest = finalDestinationRef.current;
            setWaypoint(null);
            finalDestinationRef.current = null;
            setRouteCoords(null);
            setNavSteps(null);
            setNavSummary(null);
            voiceSpokenRef.current = new Set();
            setNavDestination(finalDest);
          } else {
            speakWithFallback('You have arrived at your destination.', 'arrive', voiceOn);
          }
        }
        return;
      }

      // Is this step just noise? (continue straight, new name, no real turn)
      const isNoise = (() => {
        if (!step.type) return false;
        const t = step.type.toLowerCase();
        const txt = (step.text || '').toLowerCase();
        // "continue", "new name", "depart", "notification" = not a real turn
        if (t === 'new name' || t === 'continue' || t === 'notification') return true;
        if (t === 'depart' && idx > 0) return true; // depart only matters at start
        if (txt.startsWith('continue')) return true;
        return false;
      })();

      // For noise steps: skip voice, advance to next step silently
      if (isNoise) {
        currentNavStepRef.current = idx + 1;
        return;
      }

      // Single announcement at 200m approach, then "now" at 30m
      const approachKey = `approach_${idx}`;
      const atKey = `at_${idx}`;

      // Skip ahead if next step is closer (close/parallel roads)
      if (idx + 1 < curSteps.length) {
        const next = curSteps[idx + 1];
        if (next?.latLng) {
          const nextDist = Math.sqrt(
            Math.pow((lat - next.latLng.lat) * 111000, 2) +
            Math.pow((lng - next.latLng.lng) * 111000 * Math.cos(lat * Math.PI / 180), 2)
          );
          if (nextDist < dist) { currentNavStepRef.current = idx + 1; return; }
        }
      }

      // Find next REAL turn after this one for chained announcement
      let nextTurnText = '';
      for (let j = idx + 1; j < curSteps.length; j++) {
        const ns = curSteps[j];
        const nt = (ns?.type || '').toLowerCase();
        if (nt === 'turn' || nt === 'roundabout' || nt === 'rotary' || nt === 'end of road' || nt === 'fork' || nt === 'merge' || nt === 'arrive') {
          // Check if this next turn is close enough to chain (within 300m of current step)
          if (ns.latLng && step.latLng) {
            const chainDist = Math.sqrt(
              Math.pow((step.latLng.lat - ns.latLng.lat) * 111000, 2) +
              Math.pow((step.latLng.lng - ns.latLng.lng) * 111000 * Math.cos(step.latLng.lat * Math.PI / 180), 2)
            );
            if (chainDist < 300) nextTurnText = `, then ${ns.text}`;
          }
          break;
        }
      }

      // Time-based "now" trigger — accounts for GPS lag and TTS startup delay
      const speedMps = (speed && Number.isFinite(speed) && speed > 0) ? speed : 3;
      const timeToArrival = speedMps > 0.5 ? dist / speedMps : 999;
      // approach zone: ~15 seconds before arrival
      const approachDist = Math.max(200, Math.min(400, speedMps * 15));

      if (dist < approachDist && timeToArrival > 4 && !voiceSpokenRef.current.has(approachKey)) {
        const roundDist = Math.round(dist / 50) * 50;
        if (!window.speechSynthesis?.speaking) {
          speakWithFallback(`In ${roundDist} metres, ${step.text}${nextTurnText}`, step.type || 'straight', voiceOn);
        }
        voiceSpokenRef.current.add(approachKey);
      } else if (timeToArrival < 6 && !voiceSpokenRef.current.has(atKey)) {
        // Fire "now" ~6 seconds BEFORE arrival — compensates for GPS lag (~2s) + TTS delay (~0.5s)
        window.speechSynthesis?.cancel();
        speakWithFallback(`${step.text} now.`, step.type || 'straight', voiceOn);
        voiceSpokenRef.current.add(atKey);
        currentNavStepRef.current = idx + 1;
      }
      // Auto-advance if we've passed this step
      else if (dist > approachDist + 200 && voiceSpokenRef.current.has(approachKey)) {
        currentNavStepRef.current = idx + 1;
      }
    };
    window.addEventListener('crisis-gps', handler);
    return () => window.removeEventListener('crisis-gps', handler);
  }, [voiceOn, navDestination, waypoint]);

  // Reset voice state when navigation ends
  useEffect(() => {
    if (!navDestination) { voiceSpokenRef.current = new Set(); }
  }, [navDestination]);


  // Cleanup watch on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      if (rerouteTimerRef.current) clearTimeout(rerouteTimerRef.current);
    };
  }, []);

  // Online/Offline detection
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  // Fetch fuel stations when toggled on
  useEffect(() => {
    if (!showFuelStations || fuelStations.length > 0) return;
    const cached = localStorage.getItem('crisis_fuel_prices');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setFuelStations(parsed);
        toast.success(`${parsed.length.toLocaleString()} fuel stations loaded`);
        return;
      } catch {}
    }
    toast.info('Loading fuel stations...');
    axios.get(`${API}/offline-nav/fuel-prices`)
      .then(res => {
        if (res.data?.prices) {
          setFuelStations(res.data.prices);
          toast.success(`${res.data.prices.length.toLocaleString()} fuel stations loaded`);
          try { localStorage.setItem('crisis_fuel_prices', JSON.stringify(res.data.prices)); } catch {}
        }
      })
      .catch(() => toast.error('Failed to load fuel stations'));
  }, [showFuelStations, fuelStations.length]);

  // Geolocation on mount
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported on this device');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationLabel('My Location');
        setLocating(false);
        setLocationPermissionDenied(false);
      },
      (err) => {
        setLocating(false);
        if (err?.code === 1) {
          // Permission denied — the persistent amber banner explains the fix,
          // so don't fire a red toast on top of it.
          setLocationPermissionDenied(true);
        } else {
          const msg = err?.code === 2 ? 'Location unavailable — check GPS signal'
            : err?.code === 3 ? 'Location request timed out — try again'
            : 'Could not get your location';
          toast.error(msg);
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  // Postcode / address lookup — sets user location center for distance filtering
  const lookupPostcode = useCallback(async (pc) => {
    const clean = pc.trim().replace(/\s+/g, '');
    if (clean.length < 3) return;
    // Try Postcodes.io first (online)
    try {
      const res = await axios.get(`https://api.postcodes.io/postcodes/${encodeURIComponent(clean)}`);
      if (res.data?.status === 200 && res.data?.result) {
        const { latitude, longitude, admin_district } = res.data.result;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationLabel(admin_district || pc.toUpperCase());
        toast.success(`Location set to ${admin_district || pc.toUpperCase()}`);
        return;
      }
    } catch { /* fall through */ }
    // Try Nominatim (online)
    try {
      const res = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: { q: pc.trim() + ', UK', format: 'json', limit: 1, addressdetails: 1 },
        headers: { 'User-Agent': 'RollM8s-CrisisZone/1.0' },
      });
      if (res.data?.length > 0) {
        const place = res.data[0];
        const label = place.address?.city || place.address?.town || place.address?.suburb || place.display_name.split(',')[0];
        setUserLocation({ lat: parseFloat(place.lat), lng: parseFloat(place.lon) });
        setLocationLabel(label);
        toast.success(`Location set to ${label}`);
        return;
      }
    } catch { /* fall through */ }
    // Try offline postcode database
    try {
      const OfflineRouter = await loadOfflineRouter();
      const result = await OfflineRouter.lookupPostcode(pc);
      if (result) {
        setUserLocation({ lat: result.lat, lng: result.lng });
        setLocationLabel(result.postcode);
        toast.success(`Location set to ${result.postcode}`);
        return;
      }
    } catch { /* no offline postcodes */ }
    toast.error('Location not found — try a full postcode or address');
  }, []);

  // Auto-request location ONLY if the browser already has permission granted
  // (so returning users with allowed access get a seamless experience, but
  // first-time visitors and people who previously denied don't get hit with
  // a permission prompt the moment they land on the page).
  useEffect(() => {
    if (!navigator.permissions || !navigator.permissions.query) return;
    let cancelled = false;
    navigator.permissions.query({ name: 'geolocation' }).then(status => {
      if (cancelled) return;
      if (status.state === 'granted') {
        requestLocation();
      } else if (status.state === 'denied') {
        // Surface the banner immediately so the user knows why Near Me /
        // directions won't work, without firing any toast.
        setLocationPermissionDenied(true);
      }
      // 'prompt' = neither granted nor denied → wait for explicit user click
    }).catch(() => { /* permissions API not available; skip auto-request */ });
    return () => { cancelled = true; };
  }, [requestLocation]);

  // Show offline sync popup ONLY on first visit (never synced before)
  // After first sync, do silent incremental updates instead
  useEffect(() => {
    if (!userLocation || loading || isOffline) return;
    const synced = localStorage.getItem('crisis_offline_synced');
    if (!synced) { setShowSyncPopup(true); return; }
    // Silent incremental sync — fetch only new resources since last sync
    (async () => {
      try {
        const res = await axios.get(`${API}/crisis/resources?since=${encodeURIComponent(synced)}&limit=50000`);
        const newResources = res.data?.resources || [];
        if (newResources.length > 0) {
          const cached = JSON.parse(localStorage.getItem('crisis_cached_resources') || '[]');
          const existingIds = new Set(cached.map(r => r.resource_id));
          const fresh = newResources.filter(r => !existingIds.has(r.resource_id));
          if (fresh.length > 0) {
            const updated = [...fresh, ...cached];
            localStorage.setItem('crisis_cached_resources', JSON.stringify(updated));
            localStorage.setItem('crisis_offline_synced', new Date().toISOString());
            // Resources cached silently
          }
        }
        // Also fetch public saved locations
        const locRes = await axios.get(`${API}/crisis/saved-locations`);
        const pubLocs = locRes.data?.locations || [];
        if (pubLocs.length > 0) {
          localStorage.setItem('crisis_public_locations', JSON.stringify(pubLocs));
        }
      } catch { /* offline or failed — no problem */ }
    })();
  }, [userLocation, loading, isOffline]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('post_type', filterType);
      if (filterCat) params.set('category', filterCat);
      const resourceParams = new URLSearchParams({ limit: '50000' });
      if (resourceCategoryFilter) resourceParams.set('category', resourceCategoryFilter);
      if (userLocation && radiusMiles > 0) {
        resourceParams.set('lat', userLocation.lat.toString());
        resourceParams.set('lng', userLocation.lng.toString());
        resourceParams.set('radius_miles', radiusMiles.toString());
      }
      if (userLocation && radiusMiles > 0) {
        params.set('lat', userLocation.lat.toString());
        params.set('lng', userLocation.lng.toString());
        params.set('radius_miles', radiusMiles.toString());
      }
      const [postsRes, statsRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/crisis/posts?${params}`),
        axios.get(`${API}/crisis/stats`),
        axios.get(`${API}/crisis/resources?${resourceParams}`),
      ]);
      setPosts(postsRes.data.posts || []);
      setStats(statsRes.data);
      const fetchedResources = resourcesRes.data.resources || [];
      setResources(fetchedResources);
      try {
        localStorage.setItem('crisis_resources_cache', JSON.stringify(fetchedResources));
        localStorage.setItem('crisis_stats_cache', JSON.stringify(statsRes.data));
        localStorage.setItem('crisis_cache_time', new Date().toISOString());
        setSavedOffline(true);
      } catch { /* storage full */ }
    } catch {
      try {
        const cached = localStorage.getItem('crisis_resources_cache');
        const cachedStats = localStorage.getItem('crisis_stats_cache');
        if (cached) { setResources(JSON.parse(cached)); setSavedOffline(true); }
        if (cachedStats) setStats(JSON.parse(cachedStats));
      } catch { /* corrupt cache */ }
    } finally {
      setLoading(false);
    }
  }, [filterType, filterCat, resourceCategoryFilter, userLocation, radiusMiles]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const mapSectionRef = useRef(null);
  const applyCategorySwitch = (categoryFilter) => {
    setTab('resources');
    setResourceCategoryFilter(categoryFilter);
    setResourceView('map');
    // Silently expand to All UK if no results within current radius
    if (categoryFilter && userLocation && radiusMiles > 0) {
      const matchCount = resources.filter(r => r.category === categoryFilter).length;
      if (matchCount === 0) {
        setRadiusMiles(0);
      }
    }
    setTimeout(() => {
      mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };
  const handleCategoryCardClick = (categoryFilter) => {
    if (navDestination) {
      setPendingCategorySwitch(categoryFilter);
      return;
    }
    applyCategorySwitch(categoryFilter);
  };
  const confirmExitNav = () => {
    handleNavClear();
    if (pendingCategorySwitch) applyCategorySwitch(pendingCategorySwitch);
    setPendingCategorySwitch(null);
  };

  const handleCreatePost = async () => {
    if (!newPost.title.trim()) return toast.error('Give your post a title');
    if (!user) return toast.error('Please login first');
    setCreating(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(`${API}/crisis/posts`, { ...newPost, lat: userLocation?.lat || null, lng: userLocation?.lng || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newPost.type === 'need' ? 'Need posted — hang tight' : 'Offer posted — thank you');
      setShowCreate(false);
      setNewPost({ type: 'need', category: 'food', title: '', description: '', location_text: '', contact_method: '', urgent: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const handleAddResource = async () => {
    if (!newResource.name.trim()) return toast.error('Name the resource');
    if (!user) return toast.error('Please login first');
    setCreating(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const payload = {
        ...newResource,
        lat: newResource.lat || (userLocation ? userLocation.lat : null),
        lng: newResource.lng || (userLocation ? userLocation.lng : null),
      };
      await axios.post(`${API}/crisis/resources`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Resource added to the map');
      setShowAddResource(false);
      setNewResource({ name: '', category: 'food', description: '', address: '', hours: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add resource');
    } finally {
      setCreating(false);
    }
  };

  const handleRespond = async (post) => {
    if (!user) return toast.error('Please login first');
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(`${API}/crisis/posts/${post.post_id}/respond`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Responded to ${post.user_name}'s ${post.type}. Send them a Post-it Note to connect.`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    }
  };

  const handleResolve = async (postId) => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(`${API}/crisis/posts/${postId}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Marked as resolved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.delete(`${API}/crisis/resources/${resourceId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Resource removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const filteredPosts = search
    ? posts.filter(p => p.title.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase()))
    : posts;

  const filteredResources = resourceSearch
    ? resources.filter(r => r.name.toLowerCase().includes(resourceSearch.toLowerCase()) || r.address?.toLowerCase().includes(resourceSearch.toLowerCase()) || r.category?.toLowerCase().includes(resourceSearch.toLowerCase()) || r.description?.toLowerCase().includes(resourceSearch.toLowerCase()))
    : resources;

  const displayResources = resourceCategoryFilter
    ? filteredResources.filter(r => r.category === resourceCategoryFilter)
    : filteredResources;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar activePage="/blackout" />
        <div className="flex items-center justify-center pt-20">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: c1 }} />
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="crisis-zone-page">
      <SEO
        title="Blackout Zone"
        description="72,000+ UK emergency resources mapped with full offline sat-nav. Food banks, shelters, water, medical — navigate even without internet."
        path="/blackout"
        image="https://www.rollm8s.com/og-crisis-map.jpg"
      />
      {!mapOpen && <Navbar activePage="/blackout" />}

      {/* Persistent banner — shown whenever the OS denied location access.
          Replaces the cycle of red error toasts on every action attempt. */}
      {locationPermissionDenied && !mapOpen && (
        <div
          className="mx-auto max-w-3xl mt-4 px-4"
          data-testid="location-permission-banner"
        >
          <div className="rounded-xl border-2 border-amber-400/50 bg-amber-400/10 p-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-200">Location is blocked</p>
              <p className="text-xs text-amber-200/80 mt-0.5">
                The Blackout Zone needs your location to find the nearest food, water, fuel and medical points and to plot directions. Allow location in your device settings (or tap the lock icon next to the browser address bar), then tap Try again.
              </p>
              <button
                type="button"
                onClick={() => { setLocationPermissionDenied(false); requestLocation(); }}
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-100 underline underline-offset-2"
                data-testid="retry-location-btn"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FULLSCREEN MAP OVERLAY — Google Maps style (browse + navigation) */}
      {mapOpen && (
        <div className="fixed inset-0 z-[999]" style={{ touchAction: 'none' }} data-testid="fullscreen-map-overlay"
          onClick={(e) => {
            // Tap on map area during navigation toggles the directions bar
            if (navDestination && e.target.closest('.leaflet-container') && !e.target.closest('button, a, .leaflet-popup, .leaflet-marker-icon, .leaflet-control, input')) {
              if (navUIVisible) hideNavUI(); else showNavUI();
            }
          }}
        >
          {/* Map fills entire screen */}
          <div className="absolute inset-0" style={{ touchAction: 'none' }}>
            <ResourceMap
              resources={displayResources}
              filterCategory={resourceCategoryFilter}
              userLocation={userLocation}
              onGetDirections={handleGetDirections}
              onAddResource={(loc) => {
                setNewResource(r => ({ ...r, lat: loc.lat, lng: loc.lng, address: `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}` }));
                setDropPinMode(false);
                // Small delay to let map popup close and UI settle before opening dialog
                setTimeout(() => setShowAddResource(true), 150);
              }}
              onAddWaypoint={(loc) => {
                if (!navDestination) return;
                // Save current destination as final, navigate to waypoint first
                finalDestinationRef.current = { ...navDestination };
                setWaypoint(loc);
                setRouteCoords(null);
                setNavSteps(null);
                setNavSummary(null);
                setDropPinMode(false);
                speakWithFallback('Waypoint added. Navigating to waypoint first.', 'depart', voiceOn);
                toast.success('Waypoint added — navigating there first');
                setNavDestination({ lat: loc.lat, lng: loc.lng, name: 'Waypoint' });
              }}
              navDestination={navDestination}
              navSteps={navSteps}
              onNavSteps={handleNavSteps}
              onNavClear={handleNavClear}
              dropPinActive={dropPinMode}
              routeCoords={routeCoords}
              onRouteCoords={setRouteCoords}
              isOffline={isOffline}
              transportMode={transportMode}
              fuelStations={fuelStations}
              showFuelStations={showFuelStations}
              fullHeight
              hideControls
              onMapRef={handleMapRef}
              onUserInteract={setMapFollowPaused}
              heatmapPoints={heatmapPoints}
              showHeatmap={showHeatmap}
              roadClosures={roadClosures}
              showClosures={showClosures}
              onUpvoteClosure={handleUpvoteClosure}
              evacuationRoutes={evacuationRoutes}
              showEvacRoutes={showEvacRoutes}
              incidents={incidents}
              onIncidentVote={handleIncidentVote}
            />

            {/* ─── Waze-style proximity alert ─── */}
            <IncidentProximityAlert incidents={incidents} onRefresh={fetchCrisisLayers} />

            {/* ─── BROWSE MODE UI (no active navigation) ─── */}
            {!navDestination && (
              <>
                {/* Floating Search Bar + Close — light glass */}
                <div className="absolute top-3 left-3 right-3 z-[500] space-y-2">
                  <div className="flex gap-1.5">
                    <div className="flex-1 flex items-center rounded-xl bg-white/90 backdrop-blur-md border border-gray-200/60 shadow-lg overflow-hidden">
                      <Search className="w-4 h-4 text-gray-400 ml-3 flex-shrink-0" />
                      <input
                        type="text"
                        value={navSearch}
                        onChange={(e) => setNavSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleNavSearch()}
                        placeholder="Search place, postcode, or street..."
                        className="flex-1 bg-transparent px-2 py-2.5 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none min-w-0"
                        data-testid="map-search-input"
                      />
                      {navSearch.trim() && (
                        <button onClick={() => setNavSearch('')} className="p-1.5 mr-1 text-gray-400 hover:text-gray-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={handleNavSearch}
                        disabled={navSearching || !navSearch.trim()}
                        className="px-3 py-2.5 disabled:opacity-40 text-white text-xs font-semibold flex items-center gap-1 flex-shrink-0 rounded-r-xl"
                        style={{ backgroundColor: c1 }}
                        data-testid="map-search-go-btn"
                      >
                        {navSearching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />}
                        Go
                      </button>
                    </div>
                    <button
                      onClick={() => setMapOpen(false)}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/90 backdrop-blur-md border border-gray-200/60 shadow-lg text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
                      data-testid="close-map-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Resource category chips — light glass */}
                  <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                    {[
                      { cat: '', label: 'All', icon: MapPin },
                      { cat: 'food', label: 'Food', icon: HandHelping },
                      { cat: 'water', label: 'Water', icon: Droplets },
                      { cat: 'fuel', label: 'Fuel', icon: Fuel },
                      { cat: 'medical', label: 'Medical', icon: Heart },
                      { cat: 'shelter', label: 'Shelter', icon: Home },
                      { cat: 'charging', label: 'Charging', icon: Zap },
                    ].map(c => {
                      const Icon = c.icon;
                      const isActive = resourceCategoryFilter === c.cat;
                      return (
                        <button
                          key={c.cat}
                          onClick={() => setResourceCategoryFilter(c.cat)}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-all shadow-md flex-shrink-0 ${
                            isActive ? 'text-white' : 'bg-white/90 backdrop-blur-md border border-gray-200/60 text-gray-600'
                          }`}
                          style={isActive ? { backgroundColor: c1 } : {}}
                          data-testid={`chip-${c.cat || 'all'}`}
                        >
                          <Icon className="w-3 h-3" /> {c.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Compass calibration prompt */}
                  {needsCalibration && (
                    <div className="bg-amber-500/90 backdrop-blur-md rounded-xl px-3 py-2 flex items-center gap-2 shadow-lg">
                      <span className="text-xl">&#8734;</span>
                      <span className="text-xs font-medium text-white">Move your phone in a figure-8 to calibrate compass</span>
                    </div>
                  )}
                </div>

                {/* Resource count — bottom right */}
                <div className="absolute bottom-4 right-3 z-[500] px-2.5 py-1.5 rounded-lg bg-white/80 backdrop-blur-md border border-gray-200/60 shadow-lg">
                  <span className="text-[10px] text-gray-500 font-medium">{displayResources.length.toLocaleString()} resources</span>
                </div>

                {/* Right-side action bar — Heatmap, Report, Closures, Battery — positioned mid-right */}
                <div className="absolute right-3 z-[500] flex flex-col gap-1.5" style={{ top: '45%', transform: 'translateY(-50%)' }} data-testid="map-action-bar">
                  <button
                    onClick={() => setShowHeatmap(!showHeatmap)}
                    className={`w-10 h-10 rounded-lg shadow-lg backdrop-blur-md flex items-center justify-center transition-all ${
                      showHeatmap ? 'bg-orange-500 text-white border border-orange-400' : 'bg-white/90 border border-gray-200/60 text-gray-500 hover:text-gray-700'
                    }`}
                    title="Toggle Heatmap"
                    data-testid="heatmap-toggle"
                  >
                    <Flame className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowIncidentReport(true)}
                    className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md bg-white/90 border border-gray-200/60 text-red-500 hover:text-red-600 flex items-center justify-center transition-all"
                    title="Report Incident"
                    data-testid="report-incident-btn"
                  >
                    <AlertTriangle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowClosureReport(true)}
                    className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md bg-white/90 border border-gray-200/60 text-amber-500 hover:text-amber-600 flex items-center justify-center transition-all"
                    title="Report Road Closure"
                    data-testid="report-closure-btn"
                  >
                    <Construction className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setBatterySaverActive(true)}
                    className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md bg-white/90 border border-gray-200/60 text-green-600 hover:text-green-700 flex items-center justify-center transition-all"
                    title="Battery Saver Mode"
                    data-testid="battery-saver-btn"
                  >
                    <BatteryLow className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      const next = !dropPinMode;
                      setDropPinMode(next);
                      toast.info(next ? 'Tap the map to drop a pin' : 'Drop pin cancelled', { duration: 1500 });
                    }}
                    className={`w-10 h-10 rounded-lg shadow-lg backdrop-blur-md border flex items-center justify-center transition-all ${
                      dropPinMode ? 'text-white border-purple-400' : 'bg-white/90 border-gray-200/60 text-purple-500 hover:text-purple-600'
                    }`}
                    style={dropPinMode ? { backgroundColor: '#a855f7' } : {}}
                    title="Drop Pin"
                    data-testid="drop-pin-btn"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}

            {/* ─── TRANSPORT MODE PICKER POPUP ─── */}
            {showTransportPicker && !navDestination && (
              <div className="absolute inset-0 z-[600] flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="transport-picker-overlay">
                <div className="bg-white rounded-2xl shadow-2xl p-5 mx-6 w-full max-w-xs" data-testid="transport-picker">
                  <p className="text-sm font-semibold text-gray-800 text-center mb-4">How are you travelling?</p>
                  <div className="flex gap-2">
                    {[
                      { value: 'car', label: 'Drive', icon: Car },
                      { value: 'bike', label: 'Cycle', icon: Bike },
                      { value: 'foot', label: 'Walk', icon: Footprints },
                    ].map(m => {
                      const Icon = m.icon;
                      const active = transportMode === m.value;
                      return (
                        <button
                          key={m.value}
                          onClick={() => { setTransportMode(m.value); setShowTransportPicker(false); }}
                          className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-xl text-xs font-semibold transition-all border-2 ${
                            active ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 hover:border-gray-300 bg-gray-50'
                          }`}
                          style={active ? { backgroundColor: c1, borderColor: c1 } : {}}
                          data-testid={`transport-pick-${m.value}`}
                        >
                          <Icon className="w-6 h-6" />
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ─── NAVIGATION MODE UI (active navigation) ─── */}
            {navDestination && (
              <>
                {/* Top-left: End Navigation button — always visible */}
                <button
                  onClick={handleNavClear}
                  className="absolute top-4 left-4 z-[500] w-10 h-10 rounded-full bg-white/90 border border-gray-200/60 backdrop-blur-md shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
                  data-testid="end-navigation-btn"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Speed limit sign */}
                {currentSpeedLimit && (
                  <div className="absolute top-16 left-4 z-[500]" data-testid="speed-limit-container">
                    <SpeedLimitSign
                      speedLimit={currentSpeedLimit}
                      lat={prevGpsRef.current?.lat}
                      lng={prevGpsRef.current?.lng}
                      token={user?.token}
                    />
                  </div>
                )}

                {/* Right side — reporting & safety */}
                <div className="absolute right-3 top-28 z-[500] flex flex-col gap-1.5" data-testid="nav-action-bar">
                  <button
                    onClick={() => setShowHeatmap(!showHeatmap)}
                    className={`w-10 h-10 rounded-lg shadow-lg backdrop-blur-md flex items-center justify-center transition-all ${
                      showHeatmap ? 'bg-orange-500 text-white border border-orange-400' : 'bg-white/90 border border-gray-200/60 text-gray-500'
                    }`}
                    data-testid="nav-heatmap-toggle"
                  >
                    <Flame className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowIncidentReport(true)}
                    className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md bg-white/90 border border-gray-200/60 text-red-500 flex items-center justify-center transition-all"
                    data-testid="nav-report-incident-btn"
                  >
                    <AlertTriangle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowClosureReport(true)}
                    className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md bg-white/90 border border-gray-200/60 text-amber-500 flex items-center justify-center transition-all"
                    data-testid="nav-report-closure-btn"
                  >
                    <Construction className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setBatterySaverActive(true)}
                    className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md bg-white/90 border border-gray-200/60 text-green-600 flex items-center justify-center transition-all"
                    data-testid="nav-battery-saver-btn"
                  >
                    <BatteryLow className="w-4 h-4" />
                  </button>
                </div>

                {/* Left side low — fuel & drop pin */}
                <div className="absolute left-3 bottom-4 z-[500] flex flex-col gap-1.5" data-testid="nav-left-bar">
                  <button
                    onClick={() => {
                      const next = !showFuelStations;
                      setShowFuelStations(next);
                    }}
                    className={`w-10 h-10 rounded-lg shadow-lg backdrop-blur-md border flex items-center justify-center transition-colors ${showFuelStations ? 'text-white border-red-500/40' : 'bg-white/90 border-gray-200/60 text-gray-500 hover:text-gray-700'}`}
                    style={showFuelStations ? { backgroundColor: '#ef4444' } : {}}
                    data-testid="fullnav-fuel-toggle"
                  >
                    <Fuel className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      const next = !dropPinMode;
                      setDropPinMode(next);
                      toast.info(next ? 'Tap the map to drop a pin' : 'Drop pin cancelled', { duration: 1500 });
                    }}
                    className={`w-10 h-10 rounded-lg shadow-lg backdrop-blur-md border flex items-center justify-center transition-all ${
                      dropPinMode ? 'text-white border-purple-400' : 'bg-white/90 border-gray-200/60 text-purple-500'
                    }`}
                    style={dropPinMode ? { backgroundColor: '#a855f7' } : {}}
                    data-testid="nav-drop-pin-btn"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}

            {/* Zoom controls */}
            <div className={`absolute z-[500] flex flex-col gap-1 ${navDestination ? 'right-4 top-4' : 'left-3 bottom-28'}`} data-testid="map-zoom-controls">
              <button
                onClick={() => mapRef.current?.zoomIn()}
                className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md flex items-center justify-center transition-colors text-lg font-bold bg-white/90 border border-gray-200/60 text-gray-600 hover:text-gray-800"
                data-testid="map-zoom-in"
              >+</button>
              <button
                onClick={() => mapRef.current?.zoomOut()}
                className="w-10 h-10 rounded-lg shadow-lg backdrop-blur-md flex items-center justify-center transition-colors text-lg font-bold bg-white/90 border border-gray-200/60 text-gray-600 hover:text-gray-800"
                data-testid="map-zoom-out"
              >-</button>
            </div>

            {/* Re-center button — appears when user drags/zooms during navigation */}
            {navDestination && mapFollowPaused && (
              <button
                onClick={() => { setMapFollowPaused(false); window.dispatchEvent(new Event('crisis-recenter')); }}
                className="absolute top-24 left-3 z-[500] px-3 py-2 rounded-lg bg-white/95 border border-gray-200/60 backdrop-blur-md shadow-lg flex items-center gap-1.5 text-gray-700 font-semibold text-xs"
                data-testid="recenter-btn"
              >
                <Navigation className="w-3.5 h-3.5" style={{ color: c1 }} />
                Re-center
              </button>
            )}
          </div>

          {/* Bottom floating panel — absolute overlay at bottom of screen */}
          {navDestination && (
            <div
              className="absolute bottom-0 left-0 right-0 z-[600] border-t border-border/10 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(12px)',
                opacity: navUIVisible ? 1 : 0,
                transform: navUIVisible ? 'translateY(0)' : 'translateY(100%)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                pointerEvents: navUIVisible ? 'auto' : 'none',
              }}
              data-testid="fullnav-bottom-panel"
            >
              {navPanelMinimized ? (
                <button
                  onClick={() => setNavPanelMinimized(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left"
                  data-testid="nav-panel-expand"
                >
                  <Navigation className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-800 truncate flex-1">{navDestination?.name}</span>
                  {navSummary && (
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {(navSummary.totalDistance / 1000).toFixed(1)} km · {Math.round(navSummary.totalTime / 60)} min
                    </span>
                  )}
                  {hillWarnings.length > 0 && (
                    <span className="text-xs text-amber-500 flex-shrink-0">
                      {hillWarnings.length} hill{hillWarnings.length > 1 ? 's' : ''}
                    </span>
                  )}
                  <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
              ) : (
                <div className="max-h-[40vh] overflow-y-auto">
                  <div className="p-3 space-y-2">
                    <VoiceNavPanel
                      destination={navDestination}
                      userLocation={userLocation}
                      steps={navSteps}
                      summary={navSummary}
                      voiceOn={voiceOn}
                      setVoiceOn={setVoiceOn}
                      onClose={() => setNavPanelMinimized(true)}
                      hillWarnings={hillWarnings}
                    />
                    <ElevationProfile routeCoords={routeCoords} c1={c1} c2={c2} onHillWarnings={setHillWarnings} />
                    <VehicleProfile onProfileChange={setVehicleProfile} c1={c1} c2={c2} />
                    {routeCoords && navSummary && (
                      <FuelPlanner
                        routeCoords={routeCoords}
                        routeDistance={navSummary.totalDistance}
                        vehicleProfile={vehicleProfile}
                        c1={c1} c2={c2}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <main className={`max-w-3xl mx-auto px-4 pt-8 pb-24 space-y-3 ${mapOpen ? 'hidden' : ''}`}>

        {/* ═══ BLACKOUT ZONE TITLE + OFFLINE SAT NAV ═══ */}
        <div data-testid="crisis-hero" className="pt-2 mb-3">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl sm:text-4xl tracking-wide leading-tight">
              <span className="text-foreground">BLACKOUT</span>
              <span className="gradient-text"> ZONE</span>
            </h1>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ url: `${window.location.origin}/api/share/blackout` }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(`${window.location.origin}/api/share/blackout`);
                  toast.success('Link copied!');
                }
              }}
              className="p-2 rounded-lg hover:bg-muted/30 transition-colors"
              data-testid="share-crisis-zone"
            >
              <Share2 className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {user?.is_admin && (
          <Button
            variant="outline"
            className="w-full rounded-lg border border-border text-muted-foreground hover:bg-muted/20 text-xs h-9 mb-3"
            disabled={osmSyncing}
            onClick={async () => {
              setOsmSyncing(true);
              try {
                const token = localStorage.getItem('rollm8s_token');
                const res = await axios.post(`${API}/crisis/sync-osm`, {}, { headers: { Authorization: `Bearer ${token}` } });
                if (res.data.status === 'already_running') {
                  toast.info('OSM sync is already running — check back shortly');
                } else {
                  toast.success('OpenStreetMap sync started — this runs in the background. Check back in a few minutes.');
                }
                const pollInterval = setInterval(async () => {
                  try {
                    const statusRes = await axios.get(`${API}/crisis/sync-osm-status`);
                    if (!statusRes.data.sync_running) {
                      clearInterval(pollInterval);
                      setOsmSyncing(false);
                      const result = statusRes.data.sync_last_result;
                      if (result?.inserted !== undefined) {
                        if (result.inserted > 0) {
                          toast.success(`Sync complete: ${result.inserted} new resources added (${statusRes.data.total} total)`);
                        } else if (result.error) {
                          toast.error(result.error);
                        } else {
                          toast.info(`Sync complete — no new resources found (${statusRes.data.total} total, all up to date)`);
                        }
                      } else if (result?.error) {
                        toast.error('Sync finished with errors — some categories may have failed');
                      }
                      fetchData();
                    }
                  } catch { /* ignore polling errors */ }
                }, 10000);
                setTimeout(() => { clearInterval(pollInterval); setOsmSyncing(false); }, 600000);
              } catch (err) {
                toast.error('Failed to start OSM sync');
                setOsmSyncing(false);
              }
            }}
            data-testid="osm-sync-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${osmSyncing ? 'animate-spin' : ''}`} />
            {osmSyncing ? 'Syncing from OpenStreetMap...' : 'Sync from OpenStreetMap'}
          </Button>
        )}

        {/* Offline Data download */}
        <div className="bg-card/60 border border-border/30 rounded-xl p-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Offline Data</span>
            </div>
            {localStorage.getItem('crisis_offline_synced') && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-500/40 text-green-400">Synced</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mb-2">
            UK road network, 1.75M postcodes, and 67K+ place names for fully offline navigation.
          </p>
          <button
            onClick={() => { setShowSyncPopup(true); }}
            className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            style={{ backgroundColor: `${c1}20`, color: c1 }}
            data-testid="satnav-download-btn"
          >
            {localStorage.getItem('crisis_offline_synced') ? 'Re-sync Offline Data' : 'Prepare for Offline Use'}
          </button>
        </div>

        {/* ═══ YELLOW SECTION — OFFLINE SAT NAV (TOP) ═══ */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-wide leading-tight">
              <span className="text-foreground">OFFLINE</span>
              <span className="gradient-text"> SAT NAV</span>
            </h2>
            <div className="flex items-center gap-2">
              {/* Info button — opens welcome modal */}
              <button
                onClick={() => setShowWelcomeModal(true)}
                className="w-8 h-8 rounded-full border border-border/50 bg-card/80 backdrop-blur-sm flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors text-xs font-bold"
                data-testid="crisis-info-btn"
              >?</button>
              {/* Theme toggle */}
              <button
                data-testid="crisis-theme-toggle"
                onClick={() => setPageMode(prev => prev === 'light' ? 'dark' : 'light')}
                className="w-8 h-8 rounded-full border border-border/50 bg-card/80 backdrop-blur-sm flex items-center justify-center text-foreground hover:bg-muted transition-colors"
                title={pageMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {pageMode === 'light' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
              </button>
              {/* SOS */}
              <button
                onClick={() => setShowSOSFlasher(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white transition-all hover:scale-105 active:scale-95 shadow-lg"
                style={{ backgroundColor: '#ef4444', boxShadow: '0 0 16px rgba(239,68,68,0.4)' }}
                data-testid="sos-open-btn"
              >
                <Flashlight className="w-4 h-4" />
                SOS
              </button>
            </div>
          </div>

          {/* Resume navigation banner — top of sat nav section */}
          {showResumeNav && cachedRoute && !navDestination && (
            <div className="rounded-xl p-3 flex items-center gap-3" style={{ border: `1px solid ${c1}40`, backgroundColor: `${c1}12` }} data-testid="resume-nav-banner">
              <Navigation className="w-5 h-5 shrink-0" style={{ color: c1 }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: c1 }}>Resume navigation?</p>
                <p className="text-[11px] text-muted-foreground truncate">To {cachedRoute.destination?.name || 'your destination'}</p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" className="rounded-lg text-white text-xs h-7 px-3" style={{ backgroundColor: c1 }} onClick={() => checkMusicBeforeNav(resumeCachedRoute)} data-testid="resume-nav-btn">
                  Resume
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg text-xs h-7 px-2" style={{ borderColor: `${c1}50`, color: c1 }} onClick={() => { setShowResumeNav(false); localStorage.removeItem('crisis_active_route'); }} data-testid="dismiss-nav-btn">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Open Map button */}
          <button
            onClick={() => checkMusicBeforeNav(() => { setResourceView('map'); setMapOpen(true); setTab('resources'); setShowTransportPicker(true); })}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all active:scale-[0.98]"
            style={{ backgroundColor: c1, boxShadow: `0 4px 20px ${c1}40` }}
            data-testid="open-map-btn"
          >
            <Map className="w-5 h-5" /> Open Map
          </button>

        </div>

        {/* ═══ RESOURCES NEAR ME (moved above categories) ═══ */}
        <div className="py-3 sm:py-4">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-wide leading-tight">
            <span className="text-foreground">RESOURCES</span>
            <span className="gradient-text"> NEAR ME...</span>
          </h2>
        </div>
        <div className="space-y-2" data-testid="location-controls">
          {/* My Location + radius */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className={`rounded-full text-xs ${userLocation ? 'border-border text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}
              style={userLocation ? { backgroundColor: `${c2}30`, borderColor: c2, color: c2 } : {}}
              onClick={requestLocation}
              disabled={locating}
              data-testid="near-me-btn"
            >
              <MapPin className={`w-3.5 h-3.5 mr-1 ${locating ? 'animate-pulse' : ''}`} />
              {locating ? 'Locating...' : locationLabel || 'My Location'}
            </Button>
            <div className="flex rounded-lg border border-border overflow-hidden">
              {RADIUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setRadiusMiles(opt.value)}
                  className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${radiusMiles === opt.value ? 'text-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                  style={radiusMiles === opt.value ? { backgroundColor: `${c2}30`, color: c2 } : {}}
                  data-testid={`radius-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {/* Set location by postcode */}
          <div className="flex items-center gap-2">
            <Input
              placeholder="Or set my location (postcode or address)"
              className="h-8 text-xs bg-card border-border rounded-lg flex-1"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { lookupPostcode(postcode); setTab('resources'); setResourceView('map'); setTimeout(() => mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); } }}
              data-testid="postcode-input"
            />
            <Button
              variant="outline" size="sm"
              className="rounded-lg text-xs h-8"
              style={{ borderColor: `${c2}50`, color: c2 }}
              onClick={() => { lookupPostcode(postcode); setTab('resources'); setResourceView('map'); setTimeout(() => mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }}
              data-testid="postcode-go-btn"
            >
              Go
            </Button>
          </div>
        </div>

        {/* CATEGORY RING */}
        <CategoryRing
          stats={stats}
          resourceCategoryFilter={resourceCategoryFilter}
          onCategoryClick={handleCategoryCardClick}
        />

        {/* Action buttons: Add Resource + Map/List toggle + resource count */}
        <div className="flex gap-2 items-center flex-wrap">
          {user && (
            <button
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all hover:scale-[1.01]"
              style={{ background: 'hsl(var(--card))', borderColor: `${c2}50` }}
              onClick={() => setShowAddResource(true)}
              data-testid="add-resource-btn"
            >
              <Plus className="w-4 h-4 flex-shrink-0" style={{ color: c2 }} />
              <span className="text-foreground font-bold text-xs tracking-wider">Add Resource</span>
            </button>
          )}
          <div className="flex rounded-lg border border-border overflow-hidden flex-shrink-0">
            <button onClick={() => { setTab('resources'); setResourceView('map'); setTimeout(() => mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }}
              className={`flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium transition-colors ${resourceView === 'map' ? 'text-foreground' : 'bg-card text-muted-foreground hover:text-foreground/80'}`}
              style={resourceView === 'map' ? { backgroundColor: `${c1}30`, color: c1 } : {}}
              data-testid="resource-view-map">
              <Map className="w-3 h-3" />Map
            </button>
            <button onClick={() => { setTab('resources'); setResourceView('list'); setListLimit(50); setTimeout(() => mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500); }}
              className={`flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium transition-colors ${resourceView === 'list' ? 'text-foreground' : 'bg-card text-muted-foreground hover:text-foreground/80'}`}
              style={resourceView === 'list' ? { backgroundColor: `${c1}30`, color: c1 } : {}}
              data-testid="resource-view-list">
              <List className="w-3 h-3" />List
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground whitespace-nowrap" data-testid="resource-count">{displayResources.length.toLocaleString()} resource{displayResources.length !== 1 ? 's' : ''}{savedOffline && !isOffline ? ' (saved offline)' : ''}</p>
        </div>


        {/* OFFLINE BANNER */}
        {isOffline && (
          <div className="rounded-2xl p-4 flex items-center gap-3" style={{ backgroundColor: `${c1}15`, border: `1px solid ${c1}40` }} data-testid="offline-banner">
            <WifiOff className="w-5 h-5 shrink-0" style={{ color: c1 }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ color: c1 }}>You're Offline</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                {savedOffline
                  ? `Showing ${resources.length} cached resources. Map tiles cached for your area — navigation may still work.`
                  : 'No cached data available. Connect to the internet to load resources.'}
              </p>
            </div>
          </div>
        )}

        {/* RESUME NAVIGATION BANNER */}
        <StatsBanner stats={stats} />

        {/* Nearby Crisis Network — above Each One Help One */}
        <div className="transform scale-105 origin-left mt-3 mb-2">
          <NearbyBeacon
            userLocation={userLocation}
            isOffline={!navigator.onLine}
            color={c1}
            onNavigate={handleGetDirections}
            userName={user?.name || user?.email}
          />
        </div>

        {/* DIVIDER STATEMENT */}
        <div className="py-5 sm:py-6">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-wide leading-tight">
            <span className="text-foreground">EACH ONE</span>
            <span className="gradient-text"> HELP ONE...</span>
          </h2>
        </div>

        {/* TAB SWITCH — single button for the notice board */}
        <style>{`
          @keyframes flickerA {
            0%, 40% { box-shadow: 0 0 18px ${c1}88, 0 0 40px ${c1}40, inset 0 0 14px ${c1}18; transform: scale(1.01); }
            50%, 90% { box-shadow: inset 0 0 8px ${c1}08; transform: scale(1); }
            100% { box-shadow: 0 0 18px ${c1}88, 0 0 40px ${c1}40, inset 0 0 14px ${c1}18; transform: scale(1.01); }
          }
        `}</style>
        <button
          className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-lg border-2 transition-all hover:scale-[1.01] relative -top-3 mb-8"
          style={{
            background: 'hsl(var(--card))',
            borderColor: `${c1}80`,
            animation: 'flickerA 3s ease-in-out infinite',
          }}
          onClick={() => setTab('board')} data-testid="tab-board">
          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: c1 }} />
          <span className="text-foreground font-bold text-xs sm:text-sm tracking-wider uppercase">Needs & Offers Notice Board</span>
        </button>

        {/* BOARD TAB */}
        {tab === 'board' && (
          <div className="space-y-3">
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search posts..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-8 h-8 text-xs bg-card border-border rounded-lg" data-testid="crisis-search" />
              </div>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                className="h-8 text-xs bg-card border border-border rounded-lg px-2 text-foreground/80" data-testid="filter-type">
                <option value="">All</option>
                <option value="need">Needs</option>
                <option value="offer">Offers</option>
              </select>
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
                className="h-8 text-xs bg-card border border-border rounded-lg px-2 text-foreground/80" data-testid="filter-cat">
                <option value="">All categories</option>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {user && (
              <button className="relative w-full rounded-xl border-2 text-sm h-16 flex items-center justify-center gap-2 transition-all overflow-hidden" style={{ borderColor: `${c1}50`, backgroundColor: `${c1}15`, color: c1 }} onClick={() => setShowCreate(true)} data-testid="create-post-btn">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center opacity-50" style={{ backgroundColor: `${c1}15` }}>
                  <HandHelping className="w-5 h-5" style={{ color: c1 }} />
                </div>
                <Plus className="w-5 h-5" />Post a Need or Offer
              </button>
            )}
            {!user && (
              <div className="text-center py-3">
                <Button variant="outline" className="rounded-full text-xs" style={{ borderColor: `${c1}50`, color: c1 }} onClick={() => navigate('/login', { state: { from: '/blackout' } })}>
                  Login to post or respond
                </Button>
              </div>
            )}
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12 space-y-2">
                <Heart className="w-8 h-8 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">No active posts yet</p>
                <p className="text-xs text-muted-foreground/60">Be the first to post what you need or can offer</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPosts.map(post => (
                  <PostCard key={post.post_id} post={post} user={user} onRespond={handleRespond} onResolve={handleResolve}
                    onSendPostIt={(u) => { setPostitUser(u); setPostitOpen(true); }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* RESOURCES TAB */}
        {tab === 'resources' && (
          <div className="space-y-3 mt-6">
            {resourceCategoryFilter && (
              <div className="flex items-center gap-2">
                <Badge className="text-xs px-3 py-1" style={{ backgroundColor: MARKER_COLORS[resourceCategoryFilter] || '#6b7280' }}>
                  Showing: {RESOURCE_CATEGORIES.find(c => c.value === resourceCategoryFilter)?.label || resourceCategoryFilter}
                </Badge>
                <button onClick={() => setResourceCategoryFilter('')} className="text-xs text-muted-foreground hover:text-foreground/80 underline" data-testid="clear-resource-filter">
                  Show all
                </button>
                <p className="text-[11px] text-muted-foreground ml-auto">{displayResources.length} result{displayResources.length !== 1 ? 's' : ''}</p>
              </div>
            )}

            {/* Saved locations */}
            {savedLocations.length > 0 && (
              <>
                <div className="py-3 sm:py-4">
                  <h2 className="font-heading text-3xl sm:text-4xl tracking-wide leading-tight">
                    <span className="text-foreground">SAVED</span>
                    <span className="gradient-text"> LOCATIONS</span>
                    <span className="text-muted-foreground text-lg sm:text-xl font-normal ml-2">(offline ready)</span>
                  </h2>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="saved-locations">
                  {savedLocations.map(loc => (
                    <div key={loc.id} className="flex items-center gap-1 rounded-lg pl-2.5 pr-1 py-1.5 group" style={{ backgroundColor: `${c2}20`, border: `1px solid ${c2}66` }}>
                      <button
                        onClick={() => handleGetDirections({ lat: loc.lat, lng: loc.lng, name: loc.resolvedName || loc.name })}
                        className="text-[12px] font-semibold truncate max-w-[160px]"
                        style={{ color: c2 }}
                        data-testid={`saved-loc-${loc.id}`}
                      >
                        {loc.name}
                      </button>
                      <button
                        onClick={() => deleteSavedLocation(loc.id)}
                        className="p-0.5 rounded text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-loc-${loc.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* INLINE MAP — on-page map for browsing resources */}
            {resourceView === 'map' && !mapOpen && (
              <div className="relative -mx-4 rounded-xl overflow-hidden" style={{ height: '450px' }} ref={mapSectionRef}>
                <ResourceMap
                  resources={displayResources}
                  filterCategory={resourceCategoryFilter}
                  userLocation={userLocation}
                  onGetDirections={handleGetDirections}
                  onAddResource={(loc) => {
                    setNewResource(r => ({ ...r, lat: loc.lat, lng: loc.lng, address: `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}` }));
                    setTimeout(() => setShowAddResource(true), 150);
                  }}
                  navDestination={null}
                  navSteps={null}
                  onNavSteps={() => {}}
                  onNavClear={() => {}}
                  dropPinActive={false}
                  routeCoords={null}
                  onRouteCoords={() => {}}
                  isOffline={isOffline}
                  transportMode={transportMode}
                  fuelStations={fuelStations}
                  showFuelStations={showFuelStations}
                  fullHeight
                  hideControls
                  onMapRef={() => {}}
                  heatmapPoints={heatmapPoints}
                  showHeatmap={showHeatmap}
                  roadClosures={roadClosures}
                  showClosures={showClosures}
                  onUpvoteClosure={handleUpvoteClosure}
                  evacuationRoutes={evacuationRoutes}
                  showEvacRoutes={showEvacRoutes}
                  incidents={incidents}
                />
              </div>
            )}

            {resourceView === 'list' && (
              displayResources.length === 0 ? (
                <div ref={mapSectionRef} className="text-center py-12 space-y-2">
                  <MapPin className="w-8 h-8 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">No resources found</p>
                  <p className="text-xs text-muted-foreground/60">{resourceCategoryFilter ? 'Try a different category or clear the filter.' : 'Know a water point, food bank or warm space? Add it here.'}</p>
                </div>
              ) : (
                <div ref={mapSectionRef} className="space-y-2">
                  {displayResources.slice(0, listLimit).map(r => (
                    <ResourceCard key={r.resource_id} resource={r} user={user} onDelete={handleDeleteResource} />
                  ))}
                  {displayResources.length > listLimit && (
                    <button
                      onClick={() => setListLimit(prev => prev + 50)}
                      className="w-full py-3 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                      data-testid="load-more-resources"
                    >
                      Load more ({(displayResources.length - listLimit).toLocaleString()} remaining)
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        )}

      </main>

      {/* CREATE POST DIALOG */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Post a Need or Offer</DialogTitle>
            <DialogDescription className="text-muted-foreground">What do you need, or what can you share?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Button size="sm" className={`flex-1 rounded-lg text-xs ${newPost.type === 'need' ? 'text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                style={newPost.type === 'need' ? { backgroundColor: c1 } : {}}
                onClick={() => setNewPost(p => ({ ...p, type: 'need' }))} data-testid="post-type-need">
                <AlertTriangle className="w-3 h-3 mr-1" />I Need
              </Button>
              <Button size="sm" className={`flex-1 rounded-lg text-xs ${newPost.type === 'offer' ? 'text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                style={newPost.type === 'offer' ? { backgroundColor: c2 } : {}}
                onClick={() => setNewPost(p => ({ ...p, type: 'offer' }))} data-testid="post-type-offer">
                <Heart className="w-3 h-3 mr-1" />I Can Offer
              </Button>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {CATEGORIES.map(c => {
                  const Icon = c.icon;
                  return (
                    <button key={c.value}
                      onClick={() => setNewPost(p => ({ ...p, category: c.value }))}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border transition-colors ${
                        newPost.category === c.value ? `border-transparent text-white` : 'bg-card border-border text-muted-foreground hover:border-border/80'
                      }`}
                      style={newPost.category === c.value ? { backgroundColor: `${c1}30`, borderColor: `${c1}66`, color: c1 } : {}}
                      data-testid={`cat-${c.value}`}>
                      <Icon className="w-3 h-3" />{c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Title</Label>
              <Input value={newPost.title} onChange={e => setNewPost(p => ({ ...p, title: e.target.value }))}
                placeholder={newPost.type === 'need' ? 'e.g. Need a lift to the food bank' : 'e.g. Spare tins and rice available'}
                className="h-9 text-sm bg-card border-border rounded-lg" maxLength={100} data-testid="post-title" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Details (optional)</Label>
              <Textarea value={newPost.description} onChange={e => setNewPost(p => ({ ...p, description: e.target.value }))}
                placeholder="Any extra info..."
                className="text-sm bg-card border-border rounded-lg resize-none" rows={2} data-testid="post-description" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Area</Label>
                <Input value={newPost.location_text} onChange={e => setNewPost(p => ({ ...p, location_text: e.target.value }))}
                  placeholder="e.g. Brixton" className="h-9 text-sm bg-card border-border rounded-lg" data-testid="post-area" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Contact</Label>
                <Input value={newPost.contact_method} onChange={e => setNewPost(p => ({ ...p, contact_method: e.target.value }))}
                  placeholder="Phone or DM" className="h-9 text-sm bg-card border-border rounded-lg" data-testid="post-contact" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="post-urgent">
              <input type="checkbox" checked={newPost.urgent} onChange={e => setNewPost(p => ({ ...p, urgent: e.target.checked }))}
                className="w-4 h-4 accent-red-500 rounded" />
              <span className="text-xs font-medium" style={{ color: c1 }}>Mark as urgent</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-lg border-border text-muted-foreground">Cancel</Button>
            <Button onClick={handleCreatePost} disabled={creating}
              className="rounded-lg text-white"
              style={{ backgroundColor: newPost.type === 'need' ? c1 : c2 }}
              data-testid="submit-post-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD RESOURCE DIALOG */}
      <Dialog open={showAddResource} onOpenChange={setShowAddResource}>
        <DialogContent className="max-w-md bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Add a Community Resource</DialogTitle>
            <DialogDescription className="text-muted-foreground">Water point, food bank, warm space — help people find what they need.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-muted-foreground">Resource Name</Label>
              <Input value={newResource.name} onChange={e => setNewResource(r => ({ ...r, name: e.target.value }))}
                placeholder="e.g. Brixton Community Kitchen" className="h-9 text-sm bg-card border-border rounded-lg" data-testid="resource-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Type</Label>
              <select value={newResource.category} onChange={e => setNewResource(r => ({ ...r, category: e.target.value }))}
                className="w-full h-9 text-sm bg-card border border-border rounded-lg px-2 text-foreground/80 mt-1" data-testid="resource-category">
                {RESOURCE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Address / Location</Label>
              <Input value={newResource.address} onChange={e => setNewResource(r => ({ ...r, address: e.target.value }))}
                placeholder="Full address or rough location" className="h-9 text-sm bg-card border-border rounded-lg" data-testid="resource-address" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Opening Hours</Label>
              <Input value={newResource.hours} onChange={e => setNewResource(r => ({ ...r, hours: e.target.value }))}
                placeholder="e.g. Mon-Fri 9am-5pm" className="h-9 text-sm bg-card border-border rounded-lg" data-testid="resource-hours" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description (optional)</Label>
              <Textarea value={newResource.description} onChange={e => setNewResource(r => ({ ...r, description: e.target.value }))}
                placeholder="What's available here?" className="text-sm bg-card border-border rounded-lg resize-none" rows={2} data-testid="resource-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddResource(false)} className="rounded-lg border-border text-muted-foreground">Cancel</Button>
            <Button onClick={handleAddResource} disabled={creating} className="rounded-lg text-white" style={{ backgroundColor: c1 }} data-testid="submit-resource-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Resource'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendPostItDialog open={postitOpen} onOpenChange={setPostitOpen} preselectedUser={postitUser} />

      {/* PAUSE MUSIC PROMPT */}
      <Dialog open={showMusicPrompt} onOpenChange={(open) => { if (!open) handleMusicPromptNo(); }}>
        <DialogContent className="max-w-xs bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-base">Pause Music?</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Music is playing. Pause it so you can hear voice navigation?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" className="flex-1 rounded-lg" onClick={handleMusicPromptNo} data-testid="music-prompt-no">No</Button>
            <Button className="flex-1 rounded-lg text-white" style={{ backgroundColor: c1 }} onClick={handleMusicPromptYes} data-testid="music-prompt-yes">Yes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXIT NAVIGATION PROMPT */}
      <Dialog open={!!pendingCategorySwitch} onOpenChange={(open) => { if (!open) setPendingCategorySwitch(null); }}>
        <DialogContent className="max-w-sm bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Exit Navigation?</DialogTitle>
            <DialogDescription className="text-muted-foreground">You're currently navigating. Exit navigation to browse resources?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingCategorySwitch(null)} className="rounded-lg border-border text-muted-foreground" data-testid="nav-exit-cancel">
              Keep Navigating
            </Button>
            <Button onClick={confirmExitNav} className="rounded-lg text-white" style={{ backgroundColor: c1 }} data-testid="nav-exit-confirm">
              Exit & Browse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OFFLINE SYNC POPUP */}
      <Dialog open={showSyncPopup} onOpenChange={(open) => { if (syncPhase === 'done' || syncPhase === 'idle') setShowSyncPopup(open); }}>
        <DialogContent className="max-w-md bg-background border-border" onPointerDownOutside={(e) => { if (syncPhase !== 'done' && syncPhase !== 'idle') e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <WifiOff className="w-5 h-5" style={{ color: c1 }} />
              Prepare for Offline Use
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
              In a crisis, you may lose internet or GPS signal. Sync now so maps, resources, and navigation work fully offline.
              <span className="block mt-2 text-xs text-muted-foreground">This uses roughly <strong className="text-foreground/80">200 MB</strong> of storage — includes map tiles, routes, and the full UK road network for offline navigation.</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2" data-testid="offline-sync-popup">
            {/* Step 1: Map Tiles */}
            <div className="rounded-xl border p-4 transition-all" style={syncPhase === 'idle' ? {} : syncPhase === 'tiles' ? { borderColor: `${c1}80`, backgroundColor: `${c1}12` } : { borderColor: `${c2}50`, backgroundColor: `${c2}12` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Step 1: Sync Map & Resources</span>
                {syncPhase !== 'idle' && syncPhase !== 'tiles' && (
                  <span className="text-xs font-medium" style={{ color: c2 }}>Complete</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-3">Downloads map tiles for the UK and caches all 31,000+ resources.</p>
              {syncPhase === 'idle' && (
                <Button onClick={syncMapTiles} className="w-full rounded-lg text-white text-sm h-9" style={{ backgroundColor: c1 }} data-testid="sync-tiles-btn">
                  <Download className="w-4 h-4 mr-2" /> Sync Map Tiles
                </Button>
              )}
              {syncPhase === 'tiles' && (
                <div className="space-y-2">
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${tileProgress.total > 0 ? (tileProgress.done / tileProgress.total * 100) : 0}%`, backgroundColor: c1 }} />
                  </div>
                  <p className="text-xs text-center font-medium" style={{ color: c1 }}>
                    {tileProgress.total > 0 ? `${Math.round(tileProgress.done / tileProgress.total * 100)}% — ${tileProgress.done} / ${tileProgress.total} tiles` : 'Starting...'}
                  </p>
                </div>
              )}
            </div>

            {/* Step 2: Routes */}
            <div className="rounded-xl border p-4 transition-all" style={syncPhase === 'idle' || syncPhase === 'tiles' ? { opacity: 0.5 } : syncPhase === 'routes' || syncPhase === 'tiles_done' ? { borderColor: `${c1}80`, backgroundColor: `${c1}12` } : { borderColor: `${c2}50`, backgroundColor: `${c2}12` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Step 2: Cache Nearby Routes</span>
                {(syncPhase === 'graph' || syncPhase === 'done') && (
                  <span className="text-xs font-medium" style={{ color: c2 }}>{syncRoutesCount} routes</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-3">Pre-calculates walking routes to the 200 nearest resources so navigation works offline.</p>
              {(syncPhase === 'routes' || syncPhase === 'tiles_done') && (
                <div className="space-y-2">
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${syncRoutesTotal > 0 ? (syncRoutesDone / syncRoutesTotal * 100) : 0}%`, backgroundColor: c1 }} />
                  </div>
                  <p className="text-xs text-center font-medium" style={{ color: c1 }}>
                    {syncRoutesTotal > 0 ? `${syncRoutesDone} / ${syncRoutesTotal} routes` : 'Calculating routes...'}
                  </p>
                </div>
              )}
              {(syncPhase === 'idle' || syncPhase === 'tiles') && (
                <Button disabled className="w-full rounded-lg text-sm h-9 opacity-40" data-testid="sync-routes-btn-disabled">
                  <MapPin className="w-4 h-4 mr-2" /> Waiting for Step 1...
                </Button>
              )}
            </div>

            {/* Step 3: Road Graph */}
            <div className="rounded-xl border p-4 transition-all" style={['idle','tiles','tiles_done','routes'].includes(syncPhase) ? { opacity: 0.5 } : syncPhase === 'graph' ? { borderColor: `${c1}80`, backgroundColor: `${c1}12` } : { borderColor: `${c2}50`, backgroundColor: `${c2}12` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Step 3: Download Road Network</span>
                {syncPhase === 'done' && (
                  <span className="text-xs font-medium" style={{ color: c2 }}>Complete</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-3">Downloads the UK road network (~168 MB), 1.75M postcode locations (~15 MB), and 67K+ place names (~1 MB) so navigation and search work anywhere offline.</p>
              {syncPhase === 'graph' && graphProgress.phase === 'downloading' && (
                <div className="space-y-2">
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${graphProgress.total > 0 ? Math.min(graphProgress.done, 100) : 0}%`, backgroundColor: c1 }} />
                  </div>
                  <p className="text-xs text-center font-medium" style={{ color: c1 }}>
                    {graphProgress.label || `${graphProgress.done || 0}%`}
                  </p>
                </div>
              )}
              {syncPhase === 'graph' && graphProgress.phase && graphProgress.phase !== 'downloading' && graphProgress.phase !== 'done' && (
                <p className="text-xs text-center font-medium" style={{ color: c1 }}>
                  {graphProgress.phase === 'decompressing' ? 'Decompressing road data...' : graphProgress.phase === 'parsing' ? 'Parsing road network...' : graphProgress.phase === 'optimizing' ? 'Optimizing for offline use...' : graphProgress.phase === 'storing' ? 'Saving to device...' : graphProgress.phase === 'postcodes' ? 'Downloading postcode database...' : graphProgress.phase.startsWith('postcodes_') ? `Postcodes: ${graphProgress.phase.replace('postcodes_', '')}...` : 'Processing...'}
                </p>
              )}
              {['idle','tiles','tiles_done','routes'].includes(syncPhase) && (
                <Button disabled className="w-full rounded-lg text-sm h-9 opacity-40" data-testid="sync-graph-btn-disabled">
                  <Navigation className="w-4 h-4 mr-2" /> Waiting for Step 2...
                </Button>
              )}
            </div>

            {/* Done state */}
            {syncPhase === 'done' && (
              <div className="text-center pt-1 space-y-2">
                <p className="text-sm font-medium" style={{ color: c2 }}>You're all set for offline use.</p>
                <Button onClick={() => { setShowSyncPopup(false); setSyncPhase('idle'); }} className="rounded-lg text-white text-sm h-9 px-6" style={{ backgroundColor: c2 }} data-testid="sync-done-btn">
                  Done
                </Button>
                <button
                  onClick={async () => {
                    try {
                      const OfflineRouter = await loadOfflineRouter();
                      const diag = await OfflineRouter.diagnose();
                      toast.info(`Graph: ${diag.status} | ${diag.nodes?.toLocaleString() || 0} nodes | ${diag.edges?.toLocaleString() || 0} edges | split=${diag.split} | flags(sample): D=${diag.flags?.drive} C=${diag.flags?.cycle} W=${diag.flags?.walk}`, { duration: 10000 });
                    } catch (e) { toast.error(`Diagnostic failed: ${e.message}`); }
                  }}
                  className="text-[10px] text-muted-foreground underline"
                  data-testid="graph-diagnostic-btn"
                >
                  Run Graph Diagnostic
                </button>
              </div>
            )}

            {syncPhase !== 'done' && syncPhase !== 'idle' && (
              <p className="text-[11px] text-muted-foreground text-center">Please wait — don't close this or go offline until all steps are complete.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Public/Private Prompt */}
      <Dialog open={showSavePrompt} onOpenChange={(open) => { if (!open) { setShowSavePrompt(false); setPendingSaveCategory(''); setPendingSaveDescription(''); setPendingSaveCustomCategory(''); } }}>
        <DialogContent className="max-w-sm bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground text-lg">Share with community?</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              <strong className="text-foreground">{pendingSaveName}</strong> has been saved to your device. Share it as a resource so everyone can find it on the map.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-2">
            {/* Category picker */}
            <div>
              <label className="text-xs font-semibold text-foreground mb-1 block">Category</label>
              <select
                value={pendingSaveCategory}
                onChange={e => setPendingSaveCategory(e.target.value)}
                className="w-full h-10 rounded-lg border-2 border-border bg-card text-foreground text-sm px-3"
                data-testid="save-category-select"
              >
                <option value="">Select a category...</option>
                {RESOURCE_CATEGORIES.filter(c => c.value !== 'other').map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
                <option value="other">Other</option>
                <option value="other_custom">Other — suggest new category</option>
              </select>
            </div>

            {/* Custom category name */}
            {pendingSaveCategory === 'other_custom' && (
              <div>
                <label className="text-xs font-semibold text-foreground mb-1 block">Suggest a category name</label>
                <Input
                  placeholder="e.g. Free WiFi, Bicycle Repair..."
                  value={pendingSaveCustomCategory}
                  onChange={e => setPendingSaveCustomCategory(e.target.value)}
                  className="h-9 text-sm bg-card border-border"
                  data-testid="save-custom-category"
                />
              </div>
            )}

            {/* Optional description */}
            <div>
              <label className="text-xs font-semibold text-foreground mb-1 block">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input
                placeholder="Opening hours, notes, etc."
                value={pendingSaveDescription}
                onChange={e => setPendingSaveDescription(e.target.value)}
                className="h-9 text-sm bg-card border-border"
                data-testid="save-description"
              />
            </div>

            <Button
              onClick={handlePublicSave}
              disabled={!pendingSaveCategory}
              className="w-full disabled:opacity-40 text-white rounded-lg h-11"
              style={{ backgroundColor: c1 }}
              data-testid="save-public-btn"
            >
              Share with everyone
            </Button>
            <Button variant="outline" onClick={handlePrivateSave} className="w-full rounded-lg h-11 border-border text-muted-foreground" data-testid="save-private-btn">
              Keep private
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crisis Welcome Modal */}
      <CrisisWelcomeModal
        open={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        onStartSync={() => { setShowSyncPopup(true); syncMapTiles(); }}
        c1={c1}
        c2={c2}
        hasSynced={!!localStorage.getItem('crisis_offline_synced')}
      />

      {/* SOS Flasher */}
      <SOSFlasher
        open={showSOSFlasher}
        onClose={() => setShowSOSFlasher(false)}
        c1={c1}
        c2={c2}
      />

      {/* Incident Report Dialog */}
      <IncidentReport
        open={showIncidentReport}
        onClose={(refresh) => { setShowIncidentReport(false); if (refresh) fetchCrisisLayers(); }}
        userLocation={userLocation}
        mapCenter={mapRef.current ? { lat: mapRef.current.getCenter().lat, lng: mapRef.current.getCenter().lng } : null}
      />

      {/* Road Closure Report Dialog */}
      <RoadClosureReport
        open={showClosureReport}
        onClose={(refresh) => { setShowClosureReport(false); if (refresh) fetchCrisisLayers(); }}
        userLocation={userLocation}
        mapCenter={mapRef.current ? { lat: mapRef.current.getCenter().lat, lng: mapRef.current.getCenter().lng } : null}
      />

      {/* Battery Saver Overlay */}
      <BatterySaver
        active={batterySaverActive}
        onClose={() => setBatterySaverActive(false)}
        userLocation={userLocation}
        resources={displayResources}
        onNavigate={(dest) => { setBatterySaverActive(false); handleGetDirections(dest); }}
        onSOS={() => { setBatterySaverActive(false); setShowSOSFlasher(true); }}
        c1={c1}
      />
    </div>
  );
}

